import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Platform, Alert, Dimensions, TouchableOpacity, FlatList, ScrollView } from 'react-native';
import { YStack, XStack, H1, H2, H3, SizableText, Card, Button, Separator, H4, Input } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import { Camera, CameraView, CameraType } from 'expo-camera';
import DefaultScreen from '@components/Global/DefaultScreenView';
import CustomDialog from '@components/UI/CustomDialog';
import { useQuery } from '@tanstack/react-query';
import useGlobalUser from '@hooks/getGlobalUser';
import QRCode from 'react-native-qrcode-svg';
import MeetingShareModal, { MeetingShareModalHandle } from './components/MeetingShareModal';
import apis, { generateAIFunction } from '@api/api';
import { useMeeting } from '@hooks/useMeeting.web';
import { io, Socket } from 'socket.io-client';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
// Business logic utilities
import * as webrtcUtils from './utils/webrtcUtils';
import * as mediaStreamUtils from './utils/mediaStreamUtils';
import * as deviceUtils from './utils/deviceUtils';
import * as streamStateUtils from './utils/streamStateUtils';
import * as devicePreferencesUtils from './utils/devicePreferencesUtils';

// Remote Video Component to prevent flashing
interface RemoteVideoComponentProps {
  userId: string;
  stream: MediaStream;
  displayName: string;
  theme: boolean;
}

const RemoteVideoComponent: React.FC<RemoteVideoComponentProps> = React.memo(({ 
  userId, 
  stream, 
  displayName, 
  theme 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Assign stream only once when component mounts or stream changes
  useEffect(() => {
    if (videoRef.current && stream) {
      // Only assign if different stream to prevent flashing
      if (videoRef.current.srcObject !== stream) {
        console.log('🎥 Assigning remote stream to video element for:', userId);
        videoRef.current.srcObject = stream;
        setHasError(false);
      }
    }
  }, [stream, userId]);

  return (
    <Card
      borderRadius={20}
      padding={0}
      width={140}
      height={120}
      overflow="hidden"
      borderWidth={1}
      borderColor={isVideoPlaying ? "#10b981" : theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
      backgroundColor={theme ? "#ffffff" : "#1e293b"}
      shadowColor={theme ? "#0f172a" : "#000000"}
      shadowOffset={{ width: 0, height: 8 }}
      shadowOpacity={theme ? 0.08 : 0.25}
      shadowRadius={16}
      elevation={8}
    >
      <View style={{ position: 'relative', width: '100%', height: '100%' }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            backgroundColor: '#1f2937'
          }}
          onLoadedMetadata={() => {
            console.log('🎥 Remote video metadata loaded for:', userId);
          }}
          onPlay={() => {
            console.log('🎥 Remote video started playing for:', userId);
            setIsVideoPlaying(true);
          }}
          onPause={() => {
            setIsVideoPlaying(false);
          }}
          onError={(e) => {
            console.error('🎥 Remote video error for:', userId, e);
            setHasError(true);
            setIsVideoPlaying(false);
          }}
        />

        {/* Overlay when no video or error */}
        {(!isVideoPlaying || hasError) && (
          <View style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#374151',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <MaterialIcons
              name={hasError ? "error" : "videocam-off"}
              size={24}
              color="#9ca3af"
            />
            <SizableText
              color="#9ca3af"
              size="$2"
              textAlign="center"
              marginTop="$1"
            >
              {hasError ? 'Error' : 'Loading...'}
            </SizableText>
          </View>
        )}

        {/* Name overlay */}
        <View style={{
          position: 'absolute',
          bottom: 4,
          left: 4,
          right: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: 4,
          paddingHorizontal: 6,
          paddingVertical: 2
        }}>
          <SizableText
            color="white"
            size="$2"
            textAlign="center"
            numberOfLines={1}
            fontWeight="600"
          >
            {displayName}
          </SizableText>
        </View>

        {/* Live indicator */}
        {isVideoPlaying && !hasError && (
          <View style={{
            position: 'absolute',
            top: 4,
            right: 4,
            backgroundColor: 'rgba(16, 185, 129, 0.9)',
            borderRadius: 4,
            paddingHorizontal: 4,
            paddingVertical: 2
          }}>
            <SizableText size="$1" color="white" fontWeight="600">
              🟢 LIVE
            </SizableText>
          </View>
        )}
      </View>
    </Card>
  );
});

// WebRTC Configuration
const rtcConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

interface CallState {
  callId: string | null;
  isInCall: boolean;
  isIncoming: boolean;
  caller: any;
  callee: any;
  callType: 'video' | 'audio';
  status: 'idle' | 'calling' | 'ringing' | 'connected' | 'ended';
}

interface MediaState {
  video: boolean;
  audio: boolean;
  screenShare: boolean;
}

interface ConferenceState {
  roomId: string | null;
  isInConference: boolean;
  participants: any[];
  isHost: boolean;
  roomSettings: {
    maxParticipants: number;
    isLocked: boolean;
    allowScreenShare: boolean;
    allowChat: boolean;
  };
}

const Meet: React.FC = () => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isDesktop = screenWidth > 1024;

  // Use shared meeting hook
  const {
    callState,
    mediaState,
    conferenceState,
    chatState,
    isLoading,
    hasMediaPermission,
    connectionStatus,
    webrtcManager,
    socketManager,
    setMediaState,
    setHasMediaPermission,
    createConference,
    joinConference,
    leaveConference,
    sendMessage,
    handleTyping,
    loadChatHistory,
    joinMeetingRoom,
    getLocalMedia,
    cleanupMedia,
    initializePeerConnection,
    setChatState,
    requestDataCollection,
    onDataFormRequested,
    submitDataResponse,
    onDataCollectionResponse,
    onDataCollectionComplete,
  } = useMeeting('web');

  // Local state for web-specific features
  const meetingShareModalRef = useRef<MeetingShareModalHandle>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const localStreamRef = useRef<MediaStream | null>(null); // Like MeetingClient.tsx
  const [showSidebar, setShowSidebar] = useState(true);
  const [activeTab, setActiveTab] = useState<'data' | 'comments'>('data');
  const [selectedData, setSelectedData] = useState(null);
  const [showDataSelector, setShowDataSelector] = useState(false);
  const navigation = useNavigation();

  // WebRTC and Socket state (like MeetingClient.tsx)
  const videoSocketRef = useRef<Socket | null>(null);
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());
  const [videoConnectionStatus, setVideoConnectionStatus] = useState<'CONNECTING' | 'CONNECTED' | 'OFFLINE'>('CONNECTING');

  // Call timer state (like MeetingClient.tsx)
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);
  const [callDuration, setCallDuration] = useState<string>('00:00:00');
  const [dataLink, setDataLink] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sharedDataLink, setSharedDataLink] = useState(null);
  const [showSharedDataLink, setShowSharedDataLink] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(null);
  const [audioPermission, setAudioPermission] = useState<boolean | null>(null);
  const [cameraType, setCameraType] = useState<CameraType>('back');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [facing, setFacing] = useState<'front' | 'back'>('back');
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);

  // Device selection state for web
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
  const [selectedAudioInput, setSelectedAudioInput] = useState<string>('');
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string>('');
  const [devicesEnumerated, setDevicesEnumerated] = useState(false);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [showVideoDeviceMenu, setShowVideoDeviceMenu] = useState(false);
  const [showAudioDeviceMenu, setShowAudioDeviceMenu] = useState(false);
  const deactivateLocalMedia = useCallback(() => {
    const localRefStream = localStreamRef.current;
    const managerStream = webrtcManager?.getLocalStream?.();

    if (!localRefStream && !managerStream && !localStream) {
      return;
    }

    console.log('🔇 Deactivating local media as Meet screen lost focus');

    try {
      webrtcManager?.toggleVideo?.(false);
      webrtcManager?.toggleAudio?.(false);
    } catch (error) {
      console.warn('🔇 Failed to toggle WebRTC manager media state:', error);
    }

    // Stop tracks using utilities
    mediaStreamUtils.stopStreamTracks(localRefStream);
    mediaStreamUtils.stopStreamTracks(managerStream);
    mediaStreamUtils.stopStreamTracks(localStream);

    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }

    localStreamRef.current = null;
    setLocalStream(null);
    setIsCameraActive(false);
    setMediaState((prev) => ({ ...prev, video: false, audio: false }));
  }, [localStream, setMediaState, webrtcManager]);

  useFocusEffect(
    useCallback(() => {
      return () => {
        if (conferenceState.isInConference) {
          deactivateLocalMedia();
        }
      };
    }, [conferenceState.isInConference, deactivateLocalMedia])
  );

  // Get user ID
  const selectedId = useGlobalUser();


  // Fetch recent meetings
  const { data: recentMeetings, isLoading: meetingsLoading, error: meetingsError } = useQuery({
    queryKey: ['recentMeetings', selectedId],
    queryFn: async () => {
      console.log('🔍 Fetching recent meetings for selectedId:', selectedId);
      if (!selectedId) {
        console.warn('🔍 No selectedId available, skipping recent meetings fetch');
        return { data: { meetings: [], pagination: {} } };
      }
      
      try {
        const response = await apis.getRecentMeetings(5, 1); // Get 5 most recent meetings
        console.log('🔍 Recent meetings response:', response);
        return response;
      } catch (error) {
        console.error('🔍 Error fetching recent meetings:', error);
        return { data: { meetings: [], pagination: {} } };
      }
    },
    enabled: !!selectedId,
    retry: 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Real participants data from conference state
  const participants = conferenceState.participants || [];

  // Fetch user data folders
  const { data: dataFolderList, isLoading: dataLoading, error: dataError } = useQuery({
    queryKey: ['dataFolders', selectedId],
    queryFn: async () => {
      console.log('🔍 Fetching data folders for selectedId:', selectedId);
      if (!selectedId) {
        console.warn('🔍 No selectedId available, skipping data folders fetch');
        return [];
      }
      
      try {
        const data = await apis.getDataFolders({ userId: selectedId, dataFilter: "all" });
        console.log('🔍 Data folders response:', data);
        if (!data?.length) {
          console.log('🔍 No data folders found');
          return [];
        }
        return data;
      } catch (error) {
        console.error('🔍 Error fetching data folders:', error);
        return [];
      }
    },
    enabled: !!selectedId, // Only run query when selectedId is available
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  // Debug data loading
  useEffect(() => {
    console.log('🔍 Data folders state changed:', {
      dataFolderList: dataFolderList?.length || 0,
      dataLoading,
      dataError: dataError?.message,
      selectedId
    });
  }, [dataFolderList, dataLoading, dataError, selectedId]);

  // Filter data based on search query
  const filteredData = dataFolderList?.filter(item =>
    item.dataTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.dataType?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.dataTags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  ) || [];

  // Handle data selection
  const handleDataSelection = useCallback((dataItem) => {
    setSelectedData(dataItem);
    setShowDataSelector(false);

    // Generate data link
    const link = `${window.location.origin}/data/${dataItem._id}?meeting=${conferenceState.roomId}`;
    setDataLink(link);

    // Track data usage
    try {
      // api.trackDataView(dataItem._id); // TODO: Implement data tracking
    } catch (error) {
      console.warn('Failed to track data usage:', error);
    }
  }, [conferenceState.roomId]);

  // Request data collection from all participants
  const requestDataCollectionFromParticipants = useCallback(() => {
    if (!selectedData) {
      Alert.alert('No Data Selected', 'Please select a data collection first.');
      return;
    }

    if (!conferenceState.roomId) {
      Alert.alert('Error', 'Not connected to a meeting room.');
      return;
    }

    console.log("selectedData", selectedData);

    // Request data collection from all participants
    requestDataCollection(selectedData);
    
    Alert.alert(
      'Data Collection Requested', 
      `Data collection "${selectedData.dataTitle}" has been sent to all participants.`,
      [{ text: 'OK' }]
    );
  }, [selectedData, conferenceState.roomId, requestDataCollection]);

  // Broadcast data link to all participants
  const broadcastDataLink = useCallback(() => {
    if (!selectedData || !conferenceState.roomId) {
      Alert.alert('Error', 'Cannot broadcast: Missing data, room, or connection');
      return;
    }

    const dataInfo = {
      title: selectedData.dataTitle,
      type: selectedData.dataType,
      count: selectedData.count,
      tags: selectedData.dataTags
    };

    // Broadcast data link functionality would be implemented here
    console.log('Broadcasting data link:', { roomId: conferenceState.roomId, dataLink, dataInfo });
  }, [selectedData, conferenceState.roomId, dataLink]);

  // Join meeting room using shared hook
  const handleJoinMeetingRoom = useCallback(() => {
    if (conferenceState.roomId) {
      joinMeetingRoom(selectedId);
    }
  }, [conferenceState.roomId, selectedId, joinMeetingRoom]);

  // Chat functions using shared hook
  const handleSendMessage = useCallback(() => {
    sendMessage(selectedId);
  }, [sendMessage, selectedId]);

  const handleUserTyping = useCallback((isTyping: boolean) => {
    handleTyping(isTyping, selectedId);
  }, [handleTyping, selectedId]);

  // Share meeting function
  const shareMeeting = useCallback(() => {
    console.log('shareMeeting called', {
      roomId: conferenceState.roomId,
      modalRef: meetingShareModalRef.current,
      modalRefType: typeof meetingShareModalRef.current,
      hasOpenModal: !!meetingShareModalRef.current?.openModal
    });

    if (!conferenceState.roomId) {
      Alert.alert('Error', 'No active meeting to share');
      return;
    }

    if (!meetingShareModalRef.current) {
      console.error('Modal ref is null - component may not be mounted');
      Alert.alert('Error', 'Modal component not available');
      return;
    }

    if (!meetingShareModalRef.current.openModal) {
      console.error('Modal ref exists but openModal method is missing');
      Alert.alert('Error', 'Modal method not available');
      return;
    }

    try {
      meetingShareModalRef.current.openModal({
        roomId: conferenceState.roomId,
        meetingName: 'SmartSaaS Meeting',
        isHost: conferenceState.isHost,
        password: undefined
      });
      console.log('Modal openModal called successfully');
    } catch (error) {
      console.error('Error opening modal:', error);
      Alert.alert('Error', 'Failed to open share modal');
    }
  }, [conferenceState.roomId, conferenceState.roomSettings, conferenceState.isHost]);

  // Auto-join meeting room when conference state changes
  useEffect(() => {
    if (conferenceState.isInConference && conferenceState.roomId && selectedId) {
      console.log('🔗 Conference state changed - auto-joining meeting room...');
      joinMeetingRoom(selectedId);

      // Start timer when conference starts
      if (!callStartTime) {
        console.log('⏱️ Starting call timer on conference start...');
        setCallStartTime(new Date());
      }
    }
  }, [conferenceState.isInConference, conferenceState.roomId, selectedId, joinMeetingRoom, callStartTime]);

  // Load chat history when meeting starts
  useEffect(() => {
    if (conferenceState.isInConference && conferenceState.roomId) {
      loadChatHistory();
    }
  }, [conferenceState.isInConference, conferenceState.roomId, loadChatHistory]);

  // Render data item
  const renderDataItem = ({ item }) => (
    <TouchableOpacity
      style={{
        padding: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
        marginBottom: 8,
        backgroundColor: selectedData?._id === item._id ? (theme ? "#f0f9ff" : "#0f172a") : "transparent"
      }}
      onPress={() => handleDataSelection(item)}
    >
      <XStack alignItems="center" gap="$3">
        <MaterialIcons
          name="folder"
          size={20}
          color={theme ? "#0ea5e9" : "#38bdf8"}
        />
        <YStack flex={1}>
          <SizableText
            color={theme ? "black" : "white"}
            size="$4"
            fontWeight="600"
          >
            {item.dataTitle}
          </SizableText>
          <SizableText
            color={theme ? "black" : "white"}
            size="$3"
          >
            {item.dataType || 'General'} • {item.count || 0} items
          </SizableText>
        </YStack>
        {selectedData?._id === item._id && (
          <MaterialIcons
            name="check-circle"
            size={20}
            color={theme ? "#059669" : "#10b981"}
          />
        )}
      </XStack>
    </TouchableOpacity>
  );

  // Request media permissions
  const requestMediaPermissions = useCallback(async () => {
    try {
      console.log('Requesting camera permissions...');
      const cameraStatus = await Camera.requestCameraPermissionsAsync();
      setCameraPermission(cameraStatus.status === 'granted');

      console.log('Requesting audio permissions...');
      const audioStatus = await Camera.requestMicrophonePermissionsAsync();
      setAudioPermission(audioStatus.status === 'granted');

      const hasPermission = cameraStatus.status === 'granted' && audioStatus.status === 'granted';

      console.log('Permission request results:', {
        camera: cameraStatus.status,
        audio: audioStatus.status,
        hasPermission
      });

      setHasMediaPermission(hasPermission);

      if (!hasPermission) {
        Alert.alert(
          'Permissions Required',
          'Camera and microphone access are required for video calling.',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Settings', onPress: () => {
                Alert.alert('Settings', 'Please enable camera and microphone permissions in your browser settings.');
              }
            }
          ]
        );
      }

      return hasPermission;
    } catch (error) {
      console.error('Error requesting media permissions:', error);
      Alert.alert('Permission Error', 'Failed to request media permissions');
      return false;
    }
  }, []);

  // Toggle camera facing
  const toggleCameraFacing = useCallback(() => {
    setFacing(current => (current === 'back' ? 'front' : 'back'));
  }, []);

  // Initialize video stream directly (like MeetingClient.tsx)
  // Removed old initializeVideoStream - using getLocalMediaDirect instead

  // WebRTC functions - using utilities
  const createPeerConnection = useCallback((userId: string) => {
    return webrtcUtils.createPeerConnection({
      userId,
      localStream: localStreamRef.current,
      callbacks: {
        onRemoteStream: (userId, stream) => {
          setRemoteStreams(prev => {
            const newMap = new Map(prev);
            newMap.set(userId, stream);
            console.log('🎥 Updated remoteStreams Map:', Array.from(newMap.keys()));
            return newMap;
          });
        },
        onConnectionStateChange: (userId, state) => {
          if (state === 'failed') {
            setRemoteStreams(prev => {
              const newMap = new Map(prev);
              newMap.delete(userId);
              return newMap;
            });
          }
        }
      },
      socketManager: socketManager
    });
  }, [socketManager]);

  // Handle incoming offer
  const handleOffer = useCallback(async (data: any) => {
    const { senderId, offer } = data;
    console.log('🔗 Handling offer from:', senderId);

    let peerConnection = peerConnectionsRef.current.get(senderId);
    if (!peerConnection) {
      console.log('🔗 Creating new peer connection for:', senderId);
      peerConnection = createPeerConnection(senderId);
    }

    try {
      await peerConnection.setRemoteDescription(offer);
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);

      if (videoSocketRef.current) {
        console.log('🔗 Sending answer to:', senderId);
        videoSocketRef.current.emit('answer', {
          answer: answer,
          targetUserId: senderId
        });
      }
    } catch (error) {
      console.error('🔗 Error handling offer:', error);
    }
  }, [createPeerConnection]);

  // Handle incoming answer
  const handleAnswer = useCallback(async (data: any) => {
    const { senderId, answer } = data;
    console.log('🔗 Handling answer from:', senderId);

    const peerConnection = peerConnectionsRef.current.get(senderId);
    if (peerConnection) {
      try {
        await peerConnection.setRemoteDescription(answer);
        console.log('🔗 Answer set successfully for:', senderId);
      } catch (error) {
        console.error('🔗 Error handling answer:', error);
      }
    } else {
      console.error('🔗 No peer connection found for:', senderId);
    }
  }, []);

  // Handle ICE candidate
  const handleIceCandidate = useCallback(async (data: any) => {
    const { senderId, candidate } = data;
    console.log('🔗 Handling ICE candidate from:', senderId);

    const peerConnection = peerConnectionsRef.current.get(senderId);
    if (peerConnection) {
      try {
        await peerConnection.addIceCandidate(candidate);
        console.log('🔗 ICE candidate added successfully for:', senderId);
      } catch (error) {
        console.error('🔗 Error handling ICE candidate:', error);
      }
    } else {
      console.error('🔗 No peer connection found for ICE candidate:', senderId);
    }
  }, []);

  // Initiate WebRTC connection with a participant
  const initiateConnection = useCallback(async (userId: string) => {
    if (userId === conferenceState.roomId) return; // Don't connect to self

    console.log('🔗 Creating peer connection for:', userId);
    const peerConnection = createPeerConnection(userId);

    try {
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      if (videoSocketRef.current) {
        console.log('🔗 Sending offer to:', userId);
        videoSocketRef.current.emit('offer', {
          offer: offer,
          targetUserId: userId
        });
      }
    } catch (error) {
      console.error('🔗 Error creating offer:', error);
    }
  }, [createPeerConnection, conferenceState.roomId]);

  // Connect to video socket - REMOVED because we'll use SocketManager from useMeeting hook
  // This was causing conflicts with the SocketManager

  // Update WebRTC handlers to use SocketManager - using utilities
  const createPeerConnectionWithSocketManager = useCallback((userId: string) => {
    const pc = webrtcUtils.createPeerConnection({
      userId,
      localStream: localStreamRef.current,
      callbacks: {
        onRemoteStream: (userId, stream) => {
          setRemoteStreams(prev => {
            const newMap = new Map(prev);
            newMap.set(userId, stream);
            console.log('🎥 Updated remoteStreams Map:', Array.from(newMap.keys()));
            return newMap;
          });
        },
        onConnectionStateChange: (userId, state) => {
          if (state === 'failed') {
            setRemoteStreams(prev => {
              const newMap = new Map(prev);
              newMap.delete(userId);
              return newMap;
            });
          }
        }
      },
      socketManager: socketManager
    });
    peerConnectionsRef.current.set(userId, pc);
    return pc;
  }, [socketManager]);

  // Update initiate connection to use SocketManager - using utilities
  const initiateConnectionWithSocketManager = useCallback(async (userId: string) => {
    try {
      await webrtcUtils.initiateConnection(
        userId,
        selectedId,
        peerConnectionsRef.current,
        createPeerConnectionWithSocketManager,
        socketManager
      );
    } catch (error) {
      console.error('🔗 Error initiating connection:', error);
    }
  }, [createPeerConnectionWithSocketManager, selectedId, socketManager]);

  // Update handleOffer to use SocketManager - using utilities
  const handleOfferWithSocketManager = useCallback(async (data: any) => {
    try {
      await webrtcUtils.handleOffer(
        data,
        peerConnectionsRef.current,
        createPeerConnectionWithSocketManager,
        socketManager
      );
    } catch (error) {
      console.error('🔗 Error handling offer:', error);
    }
  }, [createPeerConnectionWithSocketManager, socketManager]);

  // Update handleAnswer to use SocketManager - using utilities
  const handleAnswerWithSocketManager = useCallback(async (data: any) => {
    try {
      await webrtcUtils.handleAnswer(data, peerConnectionsRef.current);
    } catch (error) {
      console.error('🔗 Error handling answer:', error);
    }
  }, []);

  // Update handleIceCandidate to use SocketManager - using utilities
  const handleIceCandidateWithSocketManager = useCallback(async (data: any) => {
    try {
      await webrtcUtils.handleIceCandidate(data, peerConnectionsRef.current);
    } catch (error) {
      console.error('🔗 Error handling ICE candidate:', error);
    }
  }, []);

  // Register WebRTC event handlers with SocketManager (AFTER all handlers are defined)
  useEffect(() => {
    if (socketManager.isSocketConnected()) {
      console.log('🔗 Registering WebRTC event handlers with SocketManager...');
      
      // Register WebRTC signaling event handlers
      socketManager.onOffer((data) => {
        console.log('🔗 SocketManager received offer event:', data);
        handleOfferWithSocketManager(data);
      });
      
      socketManager.onAnswer((data) => {
        console.log('🔗 SocketManager received answer event:', data);
        handleAnswerWithSocketManager(data);
      });
      
      socketManager.onIceCandidate((data) => {
        console.log('🔗 SocketManager received ice-candidate event:', data);
        handleIceCandidateWithSocketManager(data);
      });
      
      console.log('🔗 WebRTC event handlers registered with SocketManager successfully');
    }
  }, [connectionStatus, handleOfferWithSocketManager, handleAnswerWithSocketManager, handleIceCandidateWithSocketManager]);

  // Simple host video enable (exactly like MeetingClient.tsx)
  useEffect(() => {
    // Only run if we're in conference, are host, have a room ID, and don't already have a stream
    // CRITICAL: Only access devices when meeting is actually active (has roomId)
    if (conferenceState.isInConference && conferenceState.isHost && conferenceState.roomId) {
      // Check if we already have an active stream - using utilities
      const hasActiveStream = streamStateUtils.hasActiveStream(localStreamRef, localStream);

      if (!hasActiveStream) {
        console.log('🎥 Host joined - getting media stream (MeetingClient.tsx style)...');
        console.log('🎥 Selected devices:', {
          video: selectedVideoDevice || 'default',
          audio: selectedAudioInput || 'default'
        });

        let isMounted = true;

        const getHostMedia = async () => {
          // Double-check meeting is still active before accessing devices
          if (!isMounted || !conferenceState.isInConference || !conferenceState.roomId) {
            console.log('🎥 Meeting not active, skipping device access');
            return;
          }

          console.log('🎥 NEW SIMPLIFIED APPROACH - getHostMedia starting...');
          try {
            console.log('🎥 Calling navigator.mediaDevices.getUserMedia directly...');
            // Force minimum video dimensions with selected devices
            // Use current device selections (captured at the time of execution)
            const currentVideoDevice = selectedVideoDevice;
            const currentAudioDevice = selectedAudioInput;

            console.log('🎥 Getting media with devices:', {
              video: currentVideoDevice || 'default',
              audio: currentAudioDevice || 'default'
            });

            const stream = await mediaStreamUtils.getUserMediaStream({
              videoDeviceId: currentVideoDevice,
              audioDeviceId: currentAudioDevice
            });

            if (!isMounted) {
              // Component unmounted, stop the stream - using utilities
              mediaStreamUtils.stopStreamTracks(stream);
              return;
            }

            console.log('🎥 SUCCESS! Host stream obtained:', stream);
            console.log('🎥 Stream details:', {
              id: stream.id,
              active: stream.active,
              videoTracks: stream.getVideoTracks().length,
              audioTracks: stream.getAudioTracks().length
            });

            localStreamRef.current = stream;

            // Assign to video element if available
            if (localVideoRef.current) {
              console.log('🎥 Video element found, assigning stream...');
              console.log('🎥 Stream tracks:', {
                video: stream.getVideoTracks().length,
                audio: stream.getAudioTracks().length,
                videoTrack: stream.getVideoTracks()[0]?.label || 'none',
                videoTrackEnabled: stream.getVideoTracks()[0]?.enabled,
                videoTrackReadyState: stream.getVideoTracks()[0]?.readyState
              });

              // Clear any existing stream first
              if (localVideoRef.current.srcObject) {
                console.log('🎥 Clearing existing stream');
                localVideoRef.current.srcObject = null;
                await new Promise(resolve => setTimeout(resolve, 50));
              }

              // Ensure video tracks are enabled before assigning
              stream.getVideoTracks().forEach(track => {
                track.enabled = true;
                console.log('🎥 Video track state:', {
                  enabled: track.enabled,
                  readyState: track.readyState,
                  muted: track.muted,
                  label: track.label
                });
              });

              // Assign new stream
              localVideoRef.current.srcObject = stream;
              console.log('🎥 Stream assigned to video element, srcObject:', !!localVideoRef.current.srcObject);
              console.log('🎥 Video element srcObject after assignment:', {
                hasSrcObject: !!localVideoRef.current.srcObject,
                srcObjectId: (localVideoRef.current.srcObject as MediaStream)?.id,
                videoTracks: (localVideoRef.current.srcObject as MediaStream)?.getVideoTracks().length
              });

              // Wait for video to be ready, then play
              const playVideo = async () => {
                try {
                  if (localVideoRef.current && localVideoRef.current.srcObject === stream) {
                    await localVideoRef.current.play();
                    console.log('🎥 Video play successful!');
                    console.log('🎥 Video state after play:', {
                      paused: localVideoRef.current.paused,
                      readyState: localVideoRef.current.readyState,
                      videoWidth: localVideoRef.current.videoWidth,
                      videoHeight: localVideoRef.current.videoHeight
                    });
                  }
                } catch (playError: any) {
                  console.warn('🎥 Video play failed:', playError);
                  // Retry after a short delay
                  setTimeout(async () => {
                    if (isMounted && localVideoRef.current && localVideoRef.current.srcObject === stream) {
                      try {
                        await localVideoRef.current.play();
                        console.log('🎥 Video play retry successful!');
                      } catch (retryError) {
                        console.error('🎥 Video play retry failed:', retryError);
                      }
                    }
                  }, 200);
                }
              };

              // Wait for metadata before playing
              if (localVideoRef.current.readyState >= 2) {
                await playVideo();
              } else {
                const onMetadataLoaded = async () => {
                  console.log('🎥 Video metadata loaded, attempting play');
                  await playVideo();
                  localVideoRef.current?.removeEventListener('loadedmetadata', onMetadataLoaded);
                };
                localVideoRef.current.addEventListener('loadedmetadata', onMetadataLoaded);
              }

              // NOW set state AFTER video element is assigned and playing
              // This prevents the useEffect from stopping the stream
              // Use a small delay to ensure video element is ready
              setTimeout(() => {
                // Double-check stream is still active before setting state
                if (stream.active && localStreamRef.current === stream && isMounted) {
                  setLocalStream(stream);
                  setIsCameraActive(true);
                  setMediaState(prev => ({ ...prev, video: true }));
                  console.log('🎥 State updated after successful video assignment');

                  // Save device preferences after successful stream start
                  devicePreferencesUtils.saveDevicePreferences({
                    videoDeviceId: currentVideoDevice || '',
                    audioInputDeviceId: currentAudioDevice || '',
                    audioOutputDeviceId: selectedAudioOutput
                  });
                } else {
                  console.warn('🎥 Stream became inactive before state update, skipping');
                }
              }, 150);
            } else {
              console.warn('🎥 Video element not ready, will retry...');
              // Retry after a short delay
              setTimeout(() => {
                if (isMounted && localVideoRef.current && localStreamRef.current) {
                  console.log('🎥 Retrying stream assignment');
                  localVideoRef.current.srcObject = localStreamRef.current;
                  localVideoRef.current.play().catch(console.warn);

                  // Set state after retry
                  setTimeout(() => {
                    if (localStreamRef.current && localStreamRef.current.active && isMounted) {
                      setLocalStream(localStreamRef.current);
                      setIsCameraActive(true);
                      setMediaState(prev => ({ ...prev, video: true }));
                    }
                  }, 150);
                }
              }, 100);
            }

            console.log('🎥 NEW SIMPLIFIED APPROACH - Complete success!');

          } catch (error) {
            if (isMounted) {
              console.error('🎥 NEW SIMPLIFIED APPROACH - Failed:', error);
              console.error('🎥 Error name:', error.name);
              console.error('🎥 Error message:', error.message);
            }
          }
        };

        // Small delay like MeetingClient.tsx - start stream even if devices aren't fully enumerated
        const timeoutId = setTimeout(getHostMedia, 500);

        return () => {
          isMounted = false;
          clearTimeout(timeoutId);
          // Don't stop the stream here - let it continue if it was successfully created
        };
      } else {
        console.log('🎥 Host already has active stream, skipping initialization');
      }
    }
  }, [conferenceState.isInConference, conferenceState.isHost, conferenceState.roomId]); // Added roomId to ensure meeting is actually active

  // Handle participant events to initiate WebRTC connections
  useEffect(() => {
    if (conferenceState.participants.length > 0) {
      console.log('🔗 Participants updated, initiating WebRTC connections...');
      conferenceState.participants.forEach((participant) => {
        if (participant.userId && participant.userId !== selectedId) {
          console.log('🔗 Initiating connection with participant:', participant.userId);
          setTimeout(() => {
            initiateConnectionWithSocketManager(participant.userId);
          }, 1000);
        }
      });
    }
  }, [conferenceState.participants, initiateConnectionWithSocketManager, selectedId]);

  // Start/stop camera - only works when in a conference
  const toggleCamera = useCallback(async () => {
    // Only allow camera toggle when in an active conference with a room ID
    if (!conferenceState.isInConference || !conferenceState.roomId) {
      Alert.alert('Not in Conference', 'Please join or create a meeting first.');
      return;
    }

    if (!hasMediaPermission) {
      const granted = await requestMediaPermissions();
      if (!granted) {
        Alert.alert('Permission Required', 'Camera permission is required to use this feature.');
        return;
      }
    }

    if (isCameraActive) {
      // Stop camera
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
        setLocalStream(null);
      }
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
      }
      setIsCameraActive(false);
    } else {
      // Start camera - only when in conference
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: selectedVideoDevice ? { exact: selectedVideoDevice } : undefined,
          width: { min: 640, ideal: 1280 },
          height: { min: 480, ideal: 720 },
          frameRate: { min: 15, ideal: 30 }
        },
        audio: selectedAudioInput ? {
          deviceId: { exact: selectedAudioInput }
        } : true
      });
      if (stream) {
        localStreamRef.current = stream;
        setLocalStream(stream);
        setIsCameraActive(true);
        setMediaState(prev => ({ ...prev, video: true }));
      }
    }
  }, [conferenceState.isInConference, hasMediaPermission, requestMediaPermissions, isCameraActive, localStream, setMediaState, selectedVideoDevice, selectedAudioInput]);

  // Check existing permissions
  const checkMediaPermissions = useCallback(async () => {
    try {
      const cameraStatus = await Camera.getCameraPermissionsAsync();
      const audioStatus = await Camera.getMicrophonePermissionsAsync();

      console.log('Permission check results:', {
        camera: cameraStatus.status,
        audio: audioStatus.status
      });

      setCameraPermission(cameraStatus.status === 'granted');
      setAudioPermission(audioStatus.status === 'granted');

      const hasPermission = cameraStatus.status === 'granted' && audioStatus.status === 'granted';
      setHasMediaPermission(hasPermission);

      console.log('Setting hasMediaPermission to:', hasPermission);
      return hasPermission;
    } catch (error) {
      console.error('Error checking media permissions:', error);
      return false;
    }
  }, []);

  // Initialize permissions on mount
  useEffect(() => {
    checkMediaPermissions();
  }, [checkMediaPermissions]);

  // Enumerate media devices - using utilities
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await deviceUtils.enumerateMediaDevices();
      setVideoDevices(devices.videoDevices);
      setAudioInputDevices(devices.audioInputDevices);
      setAudioOutputDevices(devices.audioOutputDevices);

      // Load saved preferences first
      const savedPreferences = devicePreferencesUtils.loadDevicePreferences();
      const validatedPreferences = devicePreferencesUtils.validateDevicePreferences(
        savedPreferences,
        devices
      );

      // Use saved preferences if available, otherwise use defaults
      const defaults = deviceUtils.getDefaultDeviceSelection(
        devices,
        validatedPreferences?.videoDeviceId || selectedVideoDevice,
        validatedPreferences?.audioInputDeviceId || selectedAudioInput,
        validatedPreferences?.audioOutputDeviceId || selectedAudioOutput
      );

      // Set device selections (prioritize saved preferences, then defaults, then current)
      const finalVideoDevice = validatedPreferences?.videoDeviceId ||
        (selectedVideoDevice || defaults.videoDevice);
      const finalAudioInput = validatedPreferences?.audioInputDeviceId ||
        (selectedAudioInput || defaults.audioInput);
      const finalAudioOutput = validatedPreferences?.audioOutputDeviceId ||
        (selectedAudioOutput || defaults.audioOutput);

      if (finalVideoDevice && finalVideoDevice !== selectedVideoDevice) {
        setSelectedVideoDevice(finalVideoDevice);
      }
      if (finalAudioInput && finalAudioInput !== selectedAudioInput) {
        setSelectedAudioInput(finalAudioInput);
      }
      if (finalAudioOutput && finalAudioOutput !== selectedAudioOutput) {
        setSelectedAudioOutput(finalAudioOutput);
      }

      // Save validated preferences if they were loaded (to update storage with validated IDs)
      if (validatedPreferences) {
        devicePreferencesUtils.saveDevicePreferences(validatedPreferences);
      }

      setDevicesEnumerated(true);
    } catch (error) {
      console.error('❌ Error enumerating devices:', error);
      setDevicesEnumerated(false);
    }
  }, [selectedVideoDevice, selectedAudioInput, selectedAudioOutput]);

  // Enumerate devices when permissions are granted
  useEffect(() => {
    if (hasMediaPermission && !devicesEnumerated) {
      enumerateDevices();
    }
  }, [hasMediaPermission, devicesEnumerated, enumerateDevices]);

  // Apply device selection and restart media stream
  const applyDeviceSelection = useCallback(async (
    deviceType: 'video' | 'audioInput' | 'audioOutput',
    deviceId: string
  ) => {
    console.log(`🔧 Applying ${deviceType} device:`, deviceId);

    // Update state first
    if (deviceType === 'video') {
      setSelectedVideoDevice(deviceId);
      // Save preference
      devicePreferencesUtils.saveDevicePreferences({
        videoDeviceId: deviceId,
        audioInputDeviceId: selectedAudioInput,
        audioOutputDeviceId: selectedAudioOutput
      });
    } else if (deviceType === 'audioInput') {
      setSelectedAudioInput(deviceId);
      // Save preference
      devicePreferencesUtils.saveDevicePreferences({
        videoDeviceId: selectedVideoDevice,
        audioInputDeviceId: deviceId,
        audioOutputDeviceId: selectedAudioOutput
      });
    } else if (deviceType === 'audioOutput') {
      setSelectedAudioOutput(deviceId);
      // Save preference
      devicePreferencesUtils.saveDevicePreferences({
        videoDeviceId: selectedVideoDevice,
        audioInputDeviceId: selectedAudioInput,
        audioOutputDeviceId: deviceId
      });
      // Apply audio output device immediately (doesn't require stream restart) - using utilities
      try {
        await deviceUtils.setAudioOutputDevice(localVideoRef.current, deviceId);
        return; // Audio output doesn't need stream restart
      } catch (error) {
        console.warn('Failed to set audio output device:', error);
        Alert.alert('Device Error', 'Failed to switch speaker device');
        return;
      }
    }

    // For video and audio input, we need to restart the stream if media is active
    // BUT only if we're in an active conference with a room ID
    const hasActiveStream = streamStateUtils.hasActiveStream(localStreamRef, localStream);
    const isActiveConference = conferenceState.isInConference && conferenceState.roomId;
    const shouldRestartVideo = deviceType === 'video' && mediaState.video && hasActiveStream && isActiveConference;
    const shouldRestartAudio = deviceType === 'audioInput' && mediaState.audio && hasActiveStream && isActiveConference;

    if (shouldRestartVideo || shouldRestartAudio) {
      try {
        // Get current device selections (use the new deviceId for the changed device)
        const currentVideoDevice = deviceType === 'video' ? deviceId : selectedVideoDevice;
        const currentAudioDevice = deviceType === 'audioInput' ? deviceId : selectedAudioInput;

        // Stop all current tracks - using utilities
        mediaStreamUtils.stopStreamTracks(localStreamRef.current);
        mediaStreamUtils.stopStreamTracks(localStream);

        // Get fresh stream with updated devices - using utilities
        const constraints = streamStateUtils.createDeviceSwitchConstraints(
          deviceType,
          deviceId,
          selectedVideoDevice,
          selectedAudioInput,
          mediaState
        );

        const newStream = await navigator.mediaDevices.getUserMedia(constraints);

        console.log('🎥 New stream obtained:', {
          id: newStream.id,
          videoTracks: newStream.getVideoTracks().length,
          audioTracks: newStream.getAudioTracks().length,
          active: newStream.active
        });

        // Update refs and state
        localStreamRef.current = newStream;
        setLocalStream(newStream);

        // Force update video element
        if (localVideoRef.current) {
          console.log('🎥 Updating video element with new stream');
          // Clear old stream first
          localVideoRef.current.srcObject = null;

          // Small delay to ensure cleanup
          await new Promise(resolve => setTimeout(resolve, 50));

          // Assign new stream
          localVideoRef.current.srcObject = newStream;

          // Force play
          try {
            await localVideoRef.current.play();
            console.log('🎥 Video element updated and playing');
          } catch (playError) {
            console.error('🎥 Video play error:', playError);
            // Try again after a short delay
            setTimeout(async () => {
              if (localVideoRef.current && localVideoRef.current.srcObject === newStream) {
                try {
                  await localVideoRef.current.play();
                  console.log('🎥 Video play retry successful');
                } catch (retryError) {
                  console.error('🎥 Video play retry failed:', retryError);
                }
              }
            }, 200);
          }
        } else {
          console.warn('🎥 Video element ref is null, cannot update');
        }

        // Update peer connections with new tracks - using utilities
        webrtcUtils.replacePeerConnectionTracks(peerConnectionsRef.current, newStream);

        // Save device preferences after successful device switch
        devicePreferencesUtils.saveDevicePreferences({
          videoDeviceId: currentVideoDevice || '',
          audioInputDeviceId: currentAudioDevice || '',
          audioOutputDeviceId: selectedAudioOutput
        });

        console.log('✅ Device switched successfully during active meeting');
      } catch (error) {
        console.error('❌ Error applying device:', error);
        Alert.alert('Device Error', `Failed to switch ${deviceType} device. Please try again.`);
      }
      } else {
        // If we're in an active conference but don't have a stream, start one with the selected device
        // CRITICAL: Only start stream if meeting is actually active (has roomId)
        if (isActiveConference && !hasActiveStream) {
          console.log(`🎥 No active stream, starting stream with selected device...`);
          try {
          const newStream = await mediaStreamUtils.getUserMediaStream({
            videoDeviceId: deviceType === 'video' ? deviceId : selectedVideoDevice,
            audioDeviceId: deviceType === 'audioInput' ? deviceId : selectedAudioInput
          });

          console.log('🎥 Stream started with selected device:', {
            id: newStream.id,
            active: newStream.active,
            videoTracks: newStream.getVideoTracks().length,
            audioTracks: newStream.getAudioTracks().length
          });

          // Ensure tracks are enabled - using utilities
          mediaStreamUtils.enableVideoTracks(newStream);

          // Assign to video element FIRST before setting state - using utilities
          // This prevents the useEffect from interfering
          if (localVideoRef.current) {
            try {
              await mediaStreamUtils.assignStreamToVideoElement(
                localVideoRef.current,
                newStream,
                { clearExisting: true, retryCount: 3 }
              );
              console.log('🎥 Video stream assigned and playing');
              console.log('🎥 Stream state after assignment:', {
                srcObject: !!localVideoRef.current.srcObject,
                paused: localVideoRef.current.paused,
                readyState: localVideoRef.current.readyState,
                streamActive: (localVideoRef.current.srcObject as MediaStream)?.active
              });
            } catch (error) {
              console.error('🎥 ERROR: Failed to assign stream to video element:', error);
              return; // Don't proceed if assignment failed
            }

            // NOW set state AFTER video element is assigned and playing
            // This prevents the useEffect from stopping the stream
            // Use a small delay to ensure video element is ready
            setTimeout(() => {
              // Double-check stream is still active before setting state
              if (newStream.active && localStreamRef.current === newStream) {
                setLocalStream(newStream);
                setIsCameraActive(true);
                setMediaState(prev => ({ ...prev, video: true, audio: true }));
                console.log('🎥 State updated after successful video assignment');

                // Save device preferences after successful stream start
                devicePreferencesUtils.saveDevicePreferences({
                  videoDeviceId: deviceType === 'video' ? deviceId : selectedVideoDevice,
                  audioInputDeviceId: deviceType === 'audioInput' ? deviceId : selectedAudioInput,
                  audioOutputDeviceId: selectedAudioOutput
                });
              } else {
                console.warn('🎥 Stream became inactive before state update, skipping');
              }
            }, 150);
          } else {
            // Video element not available, set state anyway
            setLocalStream(newStream);
            setIsCameraActive(true);
            setMediaState(prev => ({ ...prev, video: true, audio: true }));
          }
        } catch (error) {
          console.error('❌ Error starting stream with selected device:', error);
          Alert.alert('Stream Error', 'Failed to start video stream with selected device');
        }
      } else {
        // Just update the selection for future use
        console.log(`✅ Device selection updated (will apply on next stream start)`);
      }
    }
  }, [localStream, mediaState, selectedVideoDevice, selectedAudioInput, conferenceState.isInConference, conferenceState.roomId]);

  // Ensure video stream is assigned when component mounts or stream changes (like MeetingClient.tsx)
  // Only run when meeting is actually active (has roomId)
  useEffect(() => {
    if (conferenceState.isInConference && conferenceState.roomId && localStreamRef.current && localVideoRef.current) {
      // Only update if the stream is different
      if (localVideoRef.current.srcObject !== localStreamRef.current) {
        console.log('🎥 Reassigning stream on conference entry');
        console.log('🎥 Current stream:', localStreamRef.current);
        console.log('🎥 Stream active:', localStreamRef.current.active);
        console.log('🎥 Video tracks:', localStreamRef.current.getVideoTracks().length);
        console.log('🎥 Video element:', localVideoRef.current);

        // Ensure video tracks are enabled
        localStreamRef.current.getVideoTracks().forEach(track => {
          track.enabled = true;
          console.log('🎥 Video track enabled:', track.enabled, 'readyState:', track.readyState, 'label:', track.label);
        });

        localVideoRef.current.srcObject = localStreamRef.current;
        console.log('🎥 Stream assigned, srcObject:', !!localVideoRef.current.srcObject);

        // Force play with retry
        const playWithRetry = async (retries = 3) => {
          try {
            if (localVideoRef.current && localVideoRef.current.srcObject === localStreamRef.current) {
              await localVideoRef.current.play();
              console.log('🎥 Video play successful on conference entry');
              console.log('🎥 Video dimensions:', {
                videoWidth: localVideoRef.current.videoWidth,
                videoHeight: localVideoRef.current.videoHeight,
                clientWidth: localVideoRef.current.clientWidth,
                clientHeight: localVideoRef.current.clientHeight
              });
            }
          } catch (error: any) {
            console.warn('🎥 Video play failed on conference entry:', error);
            if (retries > 0) {
              setTimeout(() => playWithRetry(retries - 1), 300);
            }
          }
        };

        setTimeout(() => playWithRetry(), 100);
      }
    }
  }, [conferenceState.isInConference]);

  // Watch for localStream changes and update video element (only when stream actually changes)
  useEffect(() => {
    if (localStream && localVideoRef.current) {
      // Only update if the stream is actually different
      const currentSrcObject = localVideoRef.current.srcObject as MediaStream | null;
      if (currentSrcObject?.id !== localStream.id) {
        console.log('🎥 Local stream changed, updating video element');
        console.log('🎥 New stream:', {
          id: localStream.id,
          active: localStream.active,
          videoTracks: localStream.getVideoTracks().length,
          audioTracks: localStream.getAudioTracks().length
        });

        // Check if the stream is still active before using it
        if (!localStream.active) {
          console.warn('🎥 WARNING: Stream is not active! Checking tracks...');
          localStream.getVideoTracks().forEach(track => {
            console.log('🎥 Track state:', {
              enabled: track.enabled,
              readyState: track.readyState,
              muted: track.muted,
              label: track.label
            });
          });
          // Don't proceed if stream is not active - it was likely stopped
          return;
        }

        // Update the ref as well
        localStreamRef.current = localStream;

        // Ensure video tracks are enabled
        localStream.getVideoTracks().forEach(track => {
          if (!track.enabled) {
            track.enabled = true;
            console.log('🎥 Enabling video track:', track.label, 'readyState:', track.readyState);
          }
        });

        // Clear old stream ONLY if it's different and still active
        if (currentSrcObject && currentSrcObject.id !== localStream.id && currentSrcObject.active) {
          console.log('🎥 Stopping old stream before assigning new one');
          // Stop old stream tracks - using utilities
          mediaStreamUtils.stopStreamTracks(currentSrcObject);
          localVideoRef.current.srcObject = null;
          // Small delay to ensure cleanup - use setTimeout instead of await
          setTimeout(() => {
            // Continue with assignment after delay
            if (localVideoRef.current && localStream.active) {
              localVideoRef.current.srcObject = localStream;
              const playWithRetry = async (retries = 3) => {
                try {
                  if (localVideoRef.current && localVideoRef.current.srcObject === localStream) {
                    const stream = localVideoRef.current.srcObject as MediaStream;
                    if (!stream.active) {
                      console.error('🎥 Stream became inactive before play!');
                      return;
                    }
                    await localVideoRef.current.play();
                    console.log('🎥 Video element updated and playing successfully');
                  }
                } catch (error: any) {
                  console.error('🎥 Video play error:', error);
                  if (retries > 0) {
                    setTimeout(() => playWithRetry(retries - 1), 200);
                  }
                }
              };
              setTimeout(() => playWithRetry(), 100);
            }
          }, 50);
          return; // Exit early, assignment will happen in setTimeout
        }

        // Force update video element
        console.log('🎥 Assigning new stream to video element');
        localVideoRef.current.srcObject = localStream;
        console.log('🎥 Stream assigned, srcObject:', !!localVideoRef.current.srcObject);

        // Force play with retry
        const playWithRetry = async (retries = 3) => {
          try {
            if (localVideoRef.current && localVideoRef.current.srcObject === localStream) {
              // Verify stream is still active before playing
              const stream = localVideoRef.current.srcObject as MediaStream;
              if (!stream.active) {
                console.error('🎥 Stream became inactive before play!');
                return;
              }
              await localVideoRef.current.play();
              console.log('🎥 Video element updated and playing successfully');
              console.log('🎥 Video dimensions:', {
                videoWidth: localVideoRef.current.videoWidth,
                videoHeight: localVideoRef.current.videoHeight,
                clientWidth: localVideoRef.current.clientWidth,
                clientHeight: localVideoRef.current.clientHeight
              });
            }
          } catch (error: any) {
            console.error('🎥 Video play error:', error);
            if (retries > 0) {
              setTimeout(() => playWithRetry(retries - 1), 200);
            }
          }
        };

        // Wait a bit for stream to be ready
        setTimeout(() => playWithRetry(), 100);
      }
    }
  }, [localStream?.id || '']); // Only depend on stream ID, not the entire stream object

  // Call timer effect (like MeetingClient.tsx)
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (callStartTime) {
      interval = setInterval(() => {
        const now = new Date();
        const elapsed = now.getTime() - callStartTime.getTime();

        const hours = Math.floor(elapsed / (1000 * 60 * 60));
        const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((elapsed % (1000 * 60)) / 1000);

        const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        setCallDuration(formattedTime);
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [callStartTime]);

  // Cleanup on unmount (like MeetingClient.tsx)
  useEffect(() => {
    return () => {
      if (videoSocketRef.current) {
        videoSocketRef.current.disconnect();
      }
      // Stop stream tracks - using utilities
      mediaStreamUtils.stopStreamTracks(localStreamRef.current);
      // Close all peer connections - using utilities
      webrtcUtils.closeAllPeerConnections(peerConnectionsRef.current);
      // Clear call timer
      setCallStartTime(null);
    };
  }, []);

  // All conference functions are now managed by the shared useMeeting hook

  // All media functions are now managed by the shared useMeeting hook

  // Web-specific layout for desktop
  if (conferenceState.isInConference) {
    return (
      <DefaultScreen>
        {/* Meeting Share Modal - Available in lobby view */}
        <MeetingShareModal ref={meetingShareModalRef} />
        <YStack flex={1}>
        {/* Header */}
        <XStack
          alignItems="center"
          justifyContent="space-between"
          padding="$6"
          borderBottomWidth={1}
          borderBottomColor={theme ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.1)"}
          backgroundColor={theme ? "#ffffff" : "#1e293b"}
          shadowColor={theme ? "#0f172a" : "#000000"}
          shadowOffset={{ width: 0, height: 4 }}
          shadowOpacity={theme ? 0.05 : 0.15}
          shadowRadius={12}
          elevation={4}
        >
            <XStack alignItems="center" gap="$3">
              <YStack>
                <H3 color={theme ? "black" : "white"}>Meeting Active</H3>
                <SizableText color={theme ? "black" : "white"} size="$3">Room ID: {conferenceState.roomId}</SizableText>
                <SizableText color={theme ? "black" : "white"} size="$3">Duration: {callDuration}</SizableText>
              </YStack>
            </XStack>

            <XStack alignItems="center" gap="$3">
              <Button
                backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
                color="white"
                onPress={() => {
                  console.log('Share button clicked');
                  shareMeeting();
                }}
                borderRadius={12}
                padding="$4"
                shadowColor="#8b5cf6"
                shadowOffset={{ width: 0, height: 6 }}
                shadowOpacity={0.3}
                shadowRadius={12}
                elevation={8}
                pressStyle={{ scale: 0.96 }}
              >
                <MaterialIcons name="share" size={20} color="white" />
                <SizableText color="white" marginLeft="$2">Share Meeting</SizableText>
              </Button>

              <Button
                backgroundColor={theme ? "#059669" : "#10b981"}
                color="white"
                onPress={() => {
                  console.log('Test modal button clicked');
                  setShowTestModal(true);
                }}
                borderRadius={12}
                padding="$4"
                shadowColor="#059669"
                shadowOffset={{ width: 0, height: 6 }}
                shadowOpacity={0.3}
                shadowRadius={12}
                elevation={8}
                pressStyle={{ scale: 0.96 }}
              >
                <MaterialIcons name="bug-report" size={20} color="white" />
                <SizableText color="white" marginLeft="$2">Test Modal</SizableText>
              </Button>

              <Button
                backgroundColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
                color={theme ? "black" : "white"}
                onPress={() => setShowSidebar(!showSidebar)}
                borderRadius={12}
                padding="$4"
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)"}
                pressStyle={{ scale: 0.96 }}
              >
                <MaterialIcons name="menu" size={20} color={theme ? "black" : "white"} />
              </Button>

              <Button
                backgroundColor={theme ? "#ef4444" : "#dc2626"}
                color="white"
                onPress={() => {
                  // Clean up video stream - using utilities
                  mediaStreamUtils.stopStreamTracks(localStream);
                  setLocalStream(null);
                  if (localVideoRef.current) {
                    localVideoRef.current.srcObject = null;
                  }
                  setIsCameraActive(false);
                  leaveConference();
                }}
                borderRadius={12}
                padding="$4"
                shadowColor="#ef4444"
                shadowOffset={{ width: 0, height: 6 }}
                shadowOpacity={0.3}
                shadowRadius={12}
                elevation={8}
                pressStyle={{ scale: 0.96 }}
              >
                Leave Meeting
              </Button>
            </XStack>
          </XStack>

          {/* Main Content Area */}
          <YStack flex={1}>
            {/* Top Section: Presenter + Sidebar */}
            <XStack flex={1} minHeight={400}>
              {/* Presenter Video (Top Left) */}
              <YStack flex={1} position="relative" marginRight="$4">
                <View style={{
                  flex: 1,
                  borderRadius: 20,
                  overflow: 'hidden',
                  position: 'relative',
                  minHeight: 300,
                  width: '100%',
                  height: '100%',
                  borderWidth: 1,
                  borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
                  backgroundColor: theme ? "#ffffff" : "#1e293b",
                  shadowColor: theme ? "#0f172a" : "#000000",
                  shadowOffset: { width: 0, height: 12 },
                  shadowOpacity: theme ? 0.08 : 0.25,
                  shadowRadius: 24,
                  elevation: 12
                }}>
                  {/* Host/Presenter video area - Always render when in conference (like MeetingClient.tsx) */}
                  {conferenceState.isInConference ? (
                    <video
                      ref={localVideoRef}
                      key={localStream?.id || 'video-element'}
                      autoPlay
                      muted
                      playsInline
                      controls={false}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        backgroundColor: '#1f2937',
                        minWidth: '100%',
                        minHeight: '100%',
                        display: 'block',
                        zIndex: 1
                      }}
                      onLoadedData={() => {
                        console.log('🎥 Video loaded data event');
                        if (localVideoRef.current) {
                          const stream = localVideoRef.current.srcObject as MediaStream | null;
                          console.log('🎥 Video element state on loadedData:', {
                            srcObject: !!stream,
                            srcObjectId: stream?.id,
                            paused: localVideoRef.current.paused,
                            readyState: localVideoRef.current.readyState,
                            videoWidth: localVideoRef.current.videoWidth,
                            videoHeight: localVideoRef.current.videoHeight,
                            currentTime: localVideoRef.current.currentTime,
                            videoTracks: stream?.getVideoTracks().length || 0,
                            videoTrackEnabled: stream?.getVideoTracks()[0]?.enabled,
                            videoTrackReadyState: stream?.getVideoTracks()[0]?.readyState
                          });

                          // Ensure video tracks are enabled
                          if (stream) {
                            stream.getVideoTracks().forEach(track => {
                              if (!track.enabled) {
                                console.log('🎥 Enabling video track on loadedData');
                                track.enabled = true;
                              }
                            });
                          }

                          // Force play if paused and ready
                          if (localVideoRef.current.paused && localVideoRef.current.readyState >= 2) {
                            localVideoRef.current.play()
                              .then(() => console.log('🎥 Auto-play on loadedData successful'))
                              .catch(err => console.warn('🎥 Auto-play on loadedData failed:', err));
                          }
                        }
                      }}
                      onClick={async () => {
                        console.log('🎥 Video clicked - forcing play');
                        if (localVideoRef.current) {
                          try {
                            await localVideoRef.current.play();
                            console.log('🎥 Video play forced successfully');
                          } catch (error) {
                            console.error('🎥 Video play failed:', error);
                          }
                        }
                      }}
                      onLoadedMetadata={() => {
                        console.log('🎥 Video metadata loaded');
                        if (localVideoRef.current) {
                          console.log('🎥 Video dimensions:', {
                            videoWidth: localVideoRef.current.videoWidth,
                            videoHeight: localVideoRef.current.videoHeight,
                            clientWidth: localVideoRef.current.clientWidth,
                            clientHeight: localVideoRef.current.clientHeight,
                            srcObject: !!localVideoRef.current.srcObject,
                            readyState: localVideoRef.current.readyState
                          });
                        }
                      }}
                      onCanPlay={() => {
                        console.log('🎥 Video can play event');
                        if (localVideoRef.current) {
                          const stream = localVideoRef.current.srcObject as MediaStream | null;
                          console.log('🎥 Ready to play:', {
                            readyState: localVideoRef.current.readyState,
                            paused: localVideoRef.current.paused,
                            videoWidth: localVideoRef.current.videoWidth,
                            videoHeight: localVideoRef.current.videoHeight,
                            srcObject: !!stream,
                            videoTracks: stream?.getVideoTracks().length || 0,
                            videoTrackEnabled: stream?.getVideoTracks()[0]?.enabled
                          });

                          // Force play if paused
                          if (localVideoRef.current.paused) {
                            localVideoRef.current.play()
                              .then(() => console.log('🎥 Auto-play on canPlay successful'))
                              .catch(err => console.warn('🎥 Auto-play on canPlay failed:', err));
                          }
                        }
                      }}
                      onPlay={() => {
                        console.log('🎥 Video started playing');
                        if (localVideoRef.current) {
                          console.log('🎥 Playing dimensions:', {
                            videoWidth: localVideoRef.current.videoWidth,
                            videoHeight: localVideoRef.current.videoHeight
                          });
                        }
                      }}
                      onPause={() => {
                        console.log('🎥 Video paused');
                      }}
                      onError={(e) => {
                        console.error('🎥 Video error:', e);
                        if (localVideoRef.current) {
                          console.error('🎥 Error details:', {
                            error: localVideoRef.current.error,
                            networkState: localVideoRef.current.networkState,
                            readyState: localVideoRef.current.readyState
                          });
                        }
                      }}
                    />
                  ) : (
                    <YStack
                      flex={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <MaterialIcons
                        name={mediaState.video ? "videocam" : "videocam-off"}
                        size={64}
                        color={mediaState.video ? "#38bdf8" : "#64748b"}
                      />
                      <SizableText
                        color={theme ? "black" : "white"}
                        textAlign="center"
                        size="$5"
                        marginTop="$3"
                      >
                          {conferenceState.isHost && !localStream ? 'Host Video Initializing...' :
                            mediaState.video ? 'Camera Ready' : 'Camera Off'}
                      </SizableText>
                        {!isCameraActive && !conferenceState.isHost && (
                        <SizableText
                          color={theme ? "black" : "white"}
                          textAlign="center"
                          size="$3"
                          marginTop="$2"
                        >
                          Click camera button to start
                        </SizableText>
                      )}
                        {conferenceState.isHost && !localStream && (
                          <SizableText
                            color={theme ? "black" : "white"}
                            textAlign="center"
                            size="$3"
                            marginTop="$2"
                          >
                            Auto-enabling host video...
                        </SizableText>
                      )}
                    </YStack>
                  )}

                  {/* Host/Presenter indicator */}
                  <View style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    backgroundColor: conferenceState.isHost
                      ? (theme ? "rgba(34, 197, 94, 0.9)" : "rgba(34, 197, 94, 0.8)")
                      : (theme ? "rgba(59, 130, 246, 0.9)" : "rgba(59, 130, 246, 0.8)"),
                    borderRadius: 20,
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderWidth: 1,
                    borderColor: conferenceState.isHost
                      ? "rgba(34, 197, 94, 0.3)"
                      : "rgba(59, 130, 246, 0.3)"
                  }}>
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons
                        name={conferenceState.isHost ? "star" : "person"}
                        size={16}
                        color="white"
                      />
                      <SizableText color="white" size="$3" fontWeight="600">
                        {conferenceState.isHost ? 'Host' : 'Presenter'}
                      </SizableText>
                      {conferenceState.isHost && mediaState.video && isCameraActive && (
                        <MaterialIcons name="videocam" size={14} color="white" />
                      )}
                    </XStack>
                  </View>
                </View>
              </YStack>

              {/* Right Sidebar with Tabs */}
              <YStack
                width={400}
                borderRadius={20}
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                shadowColor={theme ? "#0f172a" : "#000000"}
                shadowOffset={{ width: 0, height: 12 }}
                shadowOpacity={theme ? 0.08 : 0.25}
                shadowRadius={24}
                elevation={12}
                overflow="hidden"
              >
                {/* Tab Headers */}
                <XStack borderBottomWidth={1} borderBottomColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}>
                  <Button
                    flex={1}
                    backgroundColor={activeTab === 'data' ? (theme ? "#f0f9ff" : "#0f172a") : "transparent"}
                    borderRadius={0}
                    onPress={() => setActiveTab('data')}
                    padding="$4"
                    pressStyle={{ scale: 0.98 }}
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="data-usage" size={20} color={theme ? "black" : "white"} />
                      <SizableText color={theme ? "black" : "white"} fontWeight="600">Data</SizableText>
                    </XStack>
                  </Button>
                  <Button
                    flex={1}
                    backgroundColor={activeTab === 'comments' ? (theme ? "#f0f9ff" : "#0f172a") : "transparent"}
                    borderRadius={0}
                    onPress={() => setActiveTab('comments')}
                    padding="$4"
                    pressStyle={{ scale: 0.98 }}
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="chat" size={20} color={theme ? "black" : "white"} />
                      <SizableText color={theme ? "black" : "white"} fontWeight="600">Comments</SizableText>
                    </XStack>
                  </Button>
                </XStack>

                {/* Tab Content */}
                <YStack flex={1} padding="$4">
                  {activeTab === 'data' ? (
                    <YStack gap="$4">
                      <H4 color={theme ? "black" : "white"}>Meeting Data</H4>

                      <Card padding="$4" borderRadius={12} borderWidth={1} borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"} backgroundColor={theme ? "#f8fafc" : "#334155"} shadowColor={theme ? "#0f172a" : "#000000"} shadowOffset={{ width: 0, height: 4 }} shadowOpacity={theme ? 0.05 : 0.15} shadowRadius={8} elevation={4}>
                        <YStack gap="$2">
                          <SizableText color={theme ? "black" : "white"} size="$3">Room ID</SizableText>
                          <SizableText color={theme ? "black" : "white"} size="$4" fontFamily="monospace">
                            {conferenceState.roomId}
                          </SizableText>
                        </YStack>
                      </Card>

                      <Card padding="$4" borderRadius={12} borderWidth={1} borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"} backgroundColor={theme ? "#f8fafc" : "#334155"} shadowColor={theme ? "#0f172a" : "#000000"} shadowOffset={{ width: 0, height: 4 }} shadowOpacity={theme ? 0.05 : 0.15} shadowRadius={8} elevation={4}>
                        <YStack gap="$2">
                          <SizableText color={theme ? "black" : "white"} size="$3">Participants</SizableText>
                          <SizableText color={theme ? "black" : "white"} size="$4">
                            {participants.length} / {conferenceState.roomSettings.maxParticipants}
                          </SizableText>
                        </YStack>
                      </Card>

                      <YStack gap="$3">
                        <H4 color={theme ? "black" : "white"}>Data Collection</H4>

                        {selectedData ? (
                          <Card padding="$4" borderRadius={12} borderWidth={1} borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"} backgroundColor={theme ? "#f8fafc" : "#334155"} shadowColor={theme ? "#0f172a" : "#000000"} shadowOffset={{ width: 0, height: 4 }} shadowOpacity={theme ? 0.05 : 0.15} shadowRadius={8} elevation={4}>
                            <YStack gap="$2">
                              <XStack alignItems="center" gap="$2">
                                <MaterialIcons name="folder" size={16} color={theme ? "#0ea5e9" : "#38bdf8"} />
                                <SizableText color={theme ? "black" : "white"} size="$4" fontWeight="600">
                                  {selectedData.dataTitle}
                                </SizableText>
                              </XStack>
                              <SizableText color={theme ? "black" : "white"} size="$3">
                                {selectedData.dataType || 'General'} • {selectedData.count || 0} items
                              </SizableText>
                              <XStack gap="$2">
                                <Button
                                  backgroundColor={theme ? "#059669" : "#10b981"}
                                  color="white"
                                  size="$3"
                                  onPress={requestDataCollectionFromParticipants}
                                  borderRadius={12}
                                  flex={1}
                                  shadowColor="#059669"
                                  shadowOffset={{ width: 0, height: 4 }}
                                  shadowOpacity={0.3}
                                  shadowRadius={8}
                                  elevation={6}
                                  pressStyle={{ scale: 0.98 }}
                                >
                                  <XStack alignItems="center" gap="$1">
                                    <MaterialIcons name="assignment" size={16} color="white" />
                                    <SizableText color="white" size="$3">Collect Data</SizableText>
                                  </XStack>
                                </Button>
                                <Button
                                  backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                                  color="white"
                                  size="$3"
                                  onPress={() => {
                                    navigator.clipboard.writeText(dataLink);
                                    Alert.alert('Copied!', 'Data link copied to clipboard');
                                  }}
                                  borderRadius={12}
                                  flex={1}
                                  shadowColor="#0ea5e9"
                                  shadowOffset={{ width: 0, height: 4 }}
                                  shadowOpacity={0.3}
                                  shadowRadius={8}
                                  elevation={6}
                                  pressStyle={{ scale: 0.98 }}
                                >
                                  <XStack alignItems="center" gap="$1">
                                    <MaterialIcons name="link" size={16} color="white" />
                                    <SizableText color="white" size="$3">Copy Link</SizableText>
                                  </XStack>
                                </Button>
                              </XStack>

                              <Button
                                backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
                                color="white"
                                onPress={broadcastDataLink}
                                borderRadius={12}
                                marginTop="$2"
                                shadowColor="#8b5cf6"
                                shadowOffset={{ width: 0, height: 6 }}
                                shadowOpacity={0.4}
                                shadowRadius={12}
                                elevation={8}
                                pressStyle={{ scale: 0.96 }}
                              >
                                <XStack alignItems="center" gap="$2">
                                  <MaterialIcons name="broadcast-on-personal" size={20} color="white" />
                                  <SizableText color="white">Broadcast to Participants</SizableText>
                                </XStack>
                              </Button>
                            </YStack>
                          </Card>
                        ) : (
                          <Button
                            backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
                            color="white"
                            onPress={() => setShowDataSelector(true)}
                            borderRadius={12}
                            shadowColor="#8b5cf6"
                            shadowOffset={{ width: 0, height: 6 }}
                            shadowOpacity={0.4}
                            shadowRadius={12}
                            elevation={8}
                            pressStyle={{ scale: 0.96 }}
                          >
                            <XStack alignItems="center" gap="$2">
                              <MaterialIcons name="add" size={20} color="white" />
                              <SizableText color="white">Select Data Collection</SizableText>
                            </XStack>
                          </Button>
                        )}

                        <H4 color={theme ? "black" : "white"}>Presentation Tools</H4>

                        <Button
                          backgroundColor="#059669"
                          color="white"
                          onPress={() => {
                            // TODO: Implement whiteboard functionality
                            console.log('Whiteboard feature not yet implemented');
                          }}
                          borderRadius={8}
                        >
                          <XStack alignItems="center" gap="$2">
                            <MaterialIcons name="edit" size={20} color="white" />
                            <SizableText color="white">Whiteboard</SizableText>
                          </XStack>
                        </Button>

                        <Button
                          backgroundColor="#dc2626"
                          color="white"
                          onPress={() => {
                            // TODO: Implement recording functionality
                            console.log('Recording feature not yet implemented');
                          }}
                          borderRadius={8}
                        >
                          <XStack alignItems="center" gap="$2">
                            <MaterialIcons name="fiber-manual-record" size={20} color="white" />
                            <SizableText color="white">Record</SizableText>
                          </XStack>
                        </Button>

                        <Button
                          backgroundColor="#6366f1"
                          color="white"
                          onPress={() => {
                            // TODO: Implement screen sharing functionality
                            console.log('Screen sharing feature not yet implemented');
                          }}
                          borderRadius={8}
                        >
                          <XStack alignItems="center" gap="$2">
                            <MaterialIcons name="screen-share" size={20} color="white" />
                            <SizableText color="white">Screen Share</SizableText>
                          </XStack>
                        </Button>
                      </YStack>
                    </YStack>
                  ) : (
                    <YStack gap="$4">
                      <H4 color={theme ? "black" : "white"}>Comments & Chat</H4>

                      {/* Chat messages */}
                      <YStack gap="$3" flex={1} maxHeight={300} overflow="hidden">
                        <ScrollView
                          style={{ flex: 1 }}
                          showsVerticalScrollIndicator={false}
                          contentContainerStyle={{ paddingBottom: 10 }}
                        >
                          {chatState.messages.length === 0 ? (
                            <SizableText color={theme ? "black" : "white"} size="$3" textAlign="center" padding="$4">
                              No messages yet. Start the conversation!
                            </SizableText>
                          ) : (
                            chatState.messages.map((message, index) => (
                              <View key={message._id || index} style={{ marginBottom: 8 }}>
                                <Card
                                  padding="$3"
                                  borderRadius={8}
                                  borderWidth={1}
                                  borderColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
                                  backgroundColor={message.user._id === selectedId ? (theme ? "#f0f9ff" : "#0f172a") : "transparent"}
                                >
                                  <YStack gap="$1">
                                    <XStack alignItems="center" gap="$2">
                                      <SizableText
                                        color={theme ? "black" : "white"}
                                        size="$3"
                                        fontWeight="600"
                                      >
                                        {message.user.displayName || 'Anonymous'}
                                      </SizableText>
                                      <SizableText
                                        color={theme ? "#64748b" : "#94a3b8"}
                                        size="$2"
                                      >
                                        {new Date(message.createdAt).toLocaleTimeString()}
                                      </SizableText>
                                    </XStack>
                                    <SizableText color={theme ? "black" : "white"} size="$4">
                                      {message.text}
                                    </SizableText>
                                  </YStack>
                                </Card>
                              </View>
                            ))
                          )}
                        </ScrollView>

                        {/* Typing indicators */}
                        {chatState.typingUsers.length > 0 && (
                          <YStack padding="$2">
                            <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$3" fontStyle="italic">
                              {chatState.typingUsers.map(u => u.user.displayName).join(', ')} {chatState.typingUsers.length === 1 ? 'is' : 'are'} typing...
                            </SizableText>
                          </YStack>
                        )}
                      </YStack>

                      {/* Chat input */}
                      <XStack gap="$2" alignItems="center">
                        <YStack flex={1}>
                          <SizableText color={theme ? "black" : "white"} size="$3" marginBottom="$1">Send a message</SizableText>
                          <Input
                            placeholder="Type your message..."
                            placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                            value={chatState.newMessage}
                            onChangeText={(text) => {
                              setChatState(prev => ({ ...prev, newMessage: text }));
                              if (text.length > 0 && !chatState.isTyping) {
                                setChatState(prev => ({ ...prev, isTyping: true }));
                                handleUserTyping(true);
                              } else if (text.length === 0 && chatState.isTyping) {
                                setChatState(prev => ({ ...prev, isTyping: false }));
                                handleUserTyping(false);
                              }
                            }}
                            onBlur={() => {
                              if (chatState.isTyping) {
                                setChatState(prev => ({ ...prev, isTyping: false }));
                                handleUserTyping(false);
                              }
                            }}
                            style={{
                              borderRadius: 8,
                              borderWidth: 1,
                              borderColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
                              color: theme ? "black" : "white"
                            }}
                          />
                        </YStack>
                        <Button
                          backgroundColor="#8b5cf6"
                          color="white"
                          borderRadius={8}
                          padding="$3"
                          onPress={handleSendMessage}
                          disabled={!chatState.newMessage.trim()}
                        >
                          <MaterialIcons name="send" size={20} color="white" />
                        </Button>
                      </XStack>
                    </YStack>
                  )}
                </YStack>
              </YStack>
            </XStack>

            {/* Remote Participants Video Grid - Replaces static participants list */}
            {(remoteStreams.size > 0 || conferenceState.participants.length > 0) && (
              <YStack marginTop="$4">
                <XStack alignItems="center" gap="$3" marginBottom="$3">
                  <MaterialIcons name="groups" size={20} color={theme ? "black" : "white"} />
                  <H4 color={theme ? "black" : "white"}>
                    Remote Participants ({Math.max(remoteStreams.size, conferenceState.participants.length)})
                  </H4>
                </XStack>

                <View style={{ minHeight: 160 }}>
                  <XStack gap="$3" flexWrap="wrap" paddingHorizontal="$2">
                    {/* Show participants with video streams */}
                    {Array.from(remoteStreams.entries()).map(([userId, stream]) => {
                      const participant = conferenceState.participants.find(p => p.userId === userId);
                      const displayName = participant?.displayName || userId;
                      
                      return (
                        <RemoteVideoComponent
                          key={userId}
                          userId={userId}
                          stream={stream}
                          displayName={displayName}
                          theme={theme}
                        />
                      );
                    })}
                    
                    {/* Show participants without video streams */}
                    {conferenceState.participants
                      .filter(participant => !remoteStreams.has(participant.userId))
                      .map((participant, index) => (
                        <Card
                          key={participant.userId || index}
                          borderRadius={12}
                          padding="$3"
                          width={140}
                          height={120}
                          alignItems="center"
                          justifyContent="center"
                          borderWidth={2}
                          borderColor="#6b7280"
                          backgroundColor="#374151"
                        >
                          <YStack alignItems="center" gap="$2">
                            <View style={{
                              width: 50,
                              height: 50,
                              borderRadius: 25,
                              backgroundColor: '#6b7280',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <MaterialIcons
                                name="person"
                                size={24}
                                color="white"
                              />
                            </View>

                            <SizableText
                              color="white"
                              size="$3"
                              textAlign="center"
                              numberOfLines={1}
                              fontWeight="600"
                            >
                              {participant.displayName || 'Anonymous'}
                            </SizableText>

                            <SizableText
                              color="#94a3b8"
                              size="$2"
                              textAlign="center"
                            >
                              Connecting...
                            </SizableText>
                          </YStack>
                        </Card>
                      ))}
                  </XStack>
                </View>
              </YStack>
            )}

            {/* Bottom Controls */}
            <XStack
              justifyContent="center"
              alignItems="center"
              padding="$4"
              gap="$4"
              marginTop="$4"
              borderTopWidth={1}
              borderTopColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
            >
              {/* Microphone Button with Device Menu */}
              <View style={{ position: 'relative' }}>
                <Button
                  backgroundColor={mediaState.audio ? (theme ? "#10b981" : "#059669") : (theme ? "#ef4444" : "#dc2626")}
                  borderRadius={50}
                  width={64}
                  height={64}
                  onPress={() => {
                    // If multiple devices and menu not shown, show menu; otherwise toggle audio
                    if (audioInputDevices.length > 1 && !showAudioDeviceMenu) {
                      setShowAudioDeviceMenu(true);
                      setShowVideoDeviceMenu(false);
                    } else {
                      setMediaState(prev => ({ ...prev, audio: !prev.audio }));
                    }
                  }}
                  onLongPress={() => {
                    if (audioInputDevices.length > 1) {
                      setShowAudioDeviceMenu(!showAudioDeviceMenu);
                      setShowVideoDeviceMenu(false);
                    }
                  }}
                  shadowColor={mediaState.audio ? "#10b981" : "#ef4444"}
                  shadowOffset={{ width: 0, height: 6 }}
                  shadowOpacity={0.4}
                  shadowRadius={12}
                  elevation={8}
                  pressStyle={{ scale: 0.92 }}
                >
                  <YStack alignItems="center" justifyContent="center">
                    <MaterialIcons
                      name={mediaState.audio ? "mic" : "mic-off"}
                      size={24}
                      color="white"
                    />
                    {audioInputDevices.length > 1 && (
                      <View style={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        width: 12,
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <MaterialIcons name="arrow-drop-down" size={8} color={mediaState.audio ? "#10b981" : "#ef4444"} />
                      </View>
                    )}
                  </YStack>
                </Button>
                {showAudioDeviceMenu && audioInputDevices.length > 1 && (
                  <Card
                    position="absolute"
                    bottom={80}
                    left={0}
                    minWidth={200}
                    borderRadius={12}
                    padding="$3"
                    borderWidth={1}
                    borderColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
                    backgroundColor={theme ? "#ffffff" : "#1e293b"}
                    shadowColor={theme ? "#0f172a" : "#000000"}
                    shadowOffset={{ width: 0, height: 8 }}
                    shadowOpacity={0.15}
                    shadowRadius={16}
                    elevation={12}
                    zIndex={1000}
                  >
                    <YStack gap="$2">
                      <SizableText color={theme ? "black" : "white"} size="$3" fontWeight="600" marginBottom="$2">
                        Select Microphone
                      </SizableText>
                      {audioInputDevices.map((device) => (
                        <TouchableOpacity
                          key={device.deviceId}
                          onPress={() => {
                            applyDeviceSelection('audioInput', device.deviceId);
                            setShowAudioDeviceMenu(false);
                          }}
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            backgroundColor: selectedAudioInput === device.deviceId
                              ? (theme ? "#f0f9ff" : "#0f172a")
                              : "transparent"
                          }}
                        >
                          <XStack alignItems="center" gap="$2">
                            <MaterialIcons
                              name={selectedAudioInput === device.deviceId ? "check-circle" : "radio-button-unchecked"}
                              size={16}
                              color={selectedAudioInput === device.deviceId
                                ? (theme ? "#0ea5e9" : "#38bdf8")
                                : (theme ? "#64748b" : "#94a3b8")}
                            />
                            <SizableText
                              color={theme ? "black" : "white"}
                              size="$3"
                              numberOfLines={1}
                              flex={1}
                            >
                              {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                            </SizableText>
                          </XStack>
                        </TouchableOpacity>
                      ))}
                    </YStack>
                  </Card>
                )}
              </View>

              {/* Video Button with Device Menu */}
              <View style={{ position: 'relative' }}>
                <Button
                  backgroundColor={mediaState.video ? (theme ? "#10b981" : "#059669") : (theme ? "#ef4444" : "#dc2626")}
                  borderRadius={50}
                  width={64}
                  height={64}
                  onPress={async () => {
                    // Only allow video toggle when in an active conference with a room ID
                    if (!conferenceState.isInConference || !conferenceState.roomId) {
                      Alert.alert('Not in Conference', 'Please join or create a meeting first.');
                      return;
                    }

                    // If multiple devices and menu not shown, show menu; otherwise toggle video
                    if (videoDevices.length > 1 && !showVideoDeviceMenu) {
                      setShowVideoDeviceMenu(true);
                      setShowAudioDeviceMenu(false);
                    } else {
                      const newVideoState = !mediaState.video;
                      setMediaState(prev => ({ ...prev, video: newVideoState }));

                      if (newVideoState && !localStream) {
                        // Start video stream with selected devices - using utilities
                        // Only if meeting is still active
                        if (conferenceState.isInConference && conferenceState.roomId) {
                          const stream = await mediaStreamUtils.getUserMediaStream({
                            videoDeviceId: selectedVideoDevice,
                            audioDeviceId: selectedAudioInput
                          });
                          if (stream) {
                            localStreamRef.current = stream;
                            setLocalStream(stream);
                            setIsCameraActive(true);
                          } else {
                            setMediaState(prev => ({ ...prev, video: false }));
                          }
                        } else {
                          setMediaState(prev => ({ ...prev, video: false }));
                          Alert.alert('Meeting Not Active', 'Cannot start video - meeting is not active.');
                        }
                      } else if (!newVideoState && localStream) {
                        // Stop video stream - using utilities
                        mediaStreamUtils.stopStreamTracks(localStream);
                        setLocalStream(null);
                        if (localVideoRef.current) {
                          localVideoRef.current.srcObject = null;
                        }
                        setIsCameraActive(false);
                      }
                    }
                  }}
                  onLongPress={() => {
                    if (videoDevices.length > 1) {
                      setShowVideoDeviceMenu(!showVideoDeviceMenu);
                      setShowAudioDeviceMenu(false);
                    }
                  }}
                  shadowColor={mediaState.video ? "#10b981" : "#ef4444"}
                  shadowOffset={{ width: 0, height: 6 }}
                  shadowOpacity={0.4}
                  shadowRadius={12}
                  elevation={8}
                  pressStyle={{ scale: 0.92 }}
                >
                  <YStack alignItems="center" justifyContent="center">
                    <MaterialIcons
                      name={mediaState.video ? "videocam" : "videocam-off"}
                      size={24}
                      color="white"
                    />
                    {videoDevices.length > 1 && (
                      <View style={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        width: 12,
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <MaterialIcons name="arrow-drop-down" size={8} color={mediaState.video ? "#10b981" : "#ef4444"} />
                      </View>
                    )}
                  </YStack>
                </Button>
                {showVideoDeviceMenu && videoDevices.length > 1 && (
                  <Card
                    position="absolute"
                    bottom={80}
                    left={0}
                    minWidth={200}
                    borderRadius={12}
                    padding="$3"
                    borderWidth={1}
                    borderColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
                    backgroundColor={theme ? "#ffffff" : "#1e293b"}
                    shadowColor={theme ? "#0f172a" : "#000000"}
                    shadowOffset={{ width: 0, height: 8 }}
                    shadowOpacity={0.15}
                    shadowRadius={16}
                    elevation={12}
                    zIndex={1000}
                  >
                    <YStack gap="$2">
                      <SizableText color={theme ? "black" : "white"} size="$3" fontWeight="600" marginBottom="$2">
                        Select Camera
                      </SizableText>
                      {videoDevices.map((device) => (
                        <TouchableOpacity
                          key={device.deviceId}
                          onPress={() => {
                            applyDeviceSelection('video', device.deviceId);
                            setShowVideoDeviceMenu(false);
                          }}
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            backgroundColor: selectedVideoDevice === device.deviceId
                              ? (theme ? "#f0f9ff" : "#0f172a")
                              : "transparent"
                          }}
                        >
                          <XStack alignItems="center" gap="$2">
                            <MaterialIcons
                              name={selectedVideoDevice === device.deviceId ? "check-circle" : "radio-button-unchecked"}
                              size={16}
                              color={selectedVideoDevice === device.deviceId
                                ? (theme ? "#0ea5e9" : "#38bdf8")
                                : (theme ? "#64748b" : "#94a3b8")}
                            />
                            <SizableText
                              color={theme ? "black" : "white"}
                              size="$3"
                              numberOfLines={1}
                              flex={1}
                            >
                              {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                            </SizableText>
                          </XStack>
                        </TouchableOpacity>
                      ))}
                    </YStack>
                  </Card>
                )}
              </View>

              <Button
                backgroundColor={theme ? "#6366f1" : "#4f46e5"}
                borderRadius={50}
                width={64}
                height={64}
                onPress={() => Alert.alert('Screen Share', 'Screen sharing would start here')}
                shadowColor="#6366f1"
                shadowOffset={{ width: 0, height: 6 }}
                shadowOpacity={0.4}
                shadowRadius={12}
                elevation={8}
                pressStyle={{ scale: 0.92 }}
              >
                <MaterialIcons name="screen-share" size={24} color="white" />
              </Button>

              <Button
                backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
                borderRadius={50}
                width={64}
                height={64}
                onPress={() => {
                  enumerateDevices();
                  setShowDeviceSelector(true);
                }}
                shadowColor="#8b5cf6"
                shadowOffset={{ width: 0, height: 6 }}
                shadowOpacity={0.4}
                shadowRadius={12}
                elevation={8}
                pressStyle={{ scale: 0.92 }}
              >
                <MaterialIcons name="settings" size={24} color="white" />
              </Button>

              <Button
                backgroundColor={isCameraActive ? (theme ? "#10b981" : "#059669") : (theme ? "#f59e0b" : "#d97706")}
                borderRadius={50}
                width={64}
                height={64}
                onPress={toggleCamera}
                shadowColor={isCameraActive ? "#10b981" : "#f59e0b"}
                shadowOffset={{ width: 0, height: 6 }}
                shadowOpacity={0.4}
                shadowRadius={12}
                elevation={8}
                pressStyle={{ scale: 0.92 }}
              >
                <MaterialIcons
                  name={isCameraActive ? "videocam" : "videocam-off"}
                  size={24}
                  color="white"
                />
              </Button>

              {/* Manual Camera Test (MeetingClient.tsx style) */}
              <Button
                backgroundColor={theme ? "#3b82f6" : "#2563eb"}
                borderRadius={50}
                width={64}
                height={64}
                onPress={async () => {
                  console.log('🔧 MANUAL TEST - Starting (MeetingClient.tsx style)');
                  try {
                    console.log('🔧 Direct getUserMedia call...');
                    const stream = await navigator.mediaDevices.getUserMedia({
                      video: {
                        width: { min: 640, ideal: 1280 },
                        height: { min: 480, ideal: 720 },
                        frameRate: { min: 15, ideal: 30 }
                      },
                      audio: true
                    });

                    console.log('🔧 SUCCESS! Stream obtained:', stream);
                    console.log('🔧 Stream details:', {
                      id: stream.id,
                      active: stream.active,
                      videoTracks: stream.getVideoTracks().length,
                      audioTracks: stream.getAudioTracks().length
                    });

                    localStreamRef.current = stream;

                    if (localVideoRef.current) {
                      console.log('🔧 Assigning to video element...');
                      localVideoRef.current.srcObject = stream;
                      await localVideoRef.current.play();
                      console.log('🔧 Video play successful!');
                    }

                    setLocalStream(stream);
                    setIsCameraActive(true);
                    setMediaState(prev => ({ ...prev, video: true }));
                    console.log('🔧 MANUAL TEST - Complete success! Video should be visible now.');

                  } catch (error) {
                    console.error('🔧 MANUAL TEST - Failed:', error);
                    console.error('🔧 Error name:', error.name);
                    console.error('🔧 Error message:', error.message);
                  }
                }}
                shadowColor="#3b82f6"
                shadowOffset={{ width: 0, height: 6 }}
                shadowOpacity={0.4}
                shadowRadius={12}
                elevation={8}
                pressStyle={{ scale: 0.92 }}
              >
                <MaterialIcons name="science" size={24} color="white" />
              </Button>

              {/* WebRTC Connection Status */}
              <XStack alignItems="center" gap="$2" paddingHorizontal="$3" paddingVertical="$2"
                backgroundColor={videoConnectionStatus === 'CONNECTED' ? '#10b981' :
                  videoConnectionStatus === 'CONNECTING' ? '#f59e0b' : '#ef4444'}
                borderRadius="$4">
                <View style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'white'
                }} />
                <SizableText size="$2" color="white" fontWeight="600">
                  {videoConnectionStatus === 'CONNECTED' ? 'WebRTC Connected' :
                    videoConnectionStatus === 'CONNECTING' ? 'Connecting...' : 'Disconnected'}
                </SizableText>
              </XStack>

            </XStack>

            {/* Click outside to close device menus */}
            {(showVideoDeviceMenu || showAudioDeviceMenu) && (
              <TouchableOpacity
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999
                }}
                onPress={() => {
                  setShowVideoDeviceMenu(false);
                  setShowAudioDeviceMenu(false);
                }}
                activeOpacity={1}
              />
            )}
          </YStack>
        </YStack>

        {/* Data Selector Modal */}
        {showDataSelector && (
          <View style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <Card
              width={600}
              maxHeight="80%"
              borderRadius={20}
              padding="$6"
              borderWidth={1}
              borderColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
            >
              <YStack gap="$4">
                <XStack alignItems="center" justifyContent="space-between">
                  <H3 color={theme ? "black" : "white"}>Select Data Collection</H3>
                  <Button
                    backgroundColor="transparent"
                    onPress={() => setShowDataSelector(false)}
                  >
                    <MaterialIcons name="close" size={24} color={theme ? "black" : "white"} />
                  </Button>
                </XStack>

                <Input
                  placeholder="Search data collections..."
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  borderWidth={1}
                  borderColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
                />

                <View style={{ height: 400 }}>
                  <FlatList
                    data={filteredData}
                    renderItem={renderDataItem}
                    keyExtractor={(item) => item._id}
                    showsVerticalScrollIndicator={false}
                    ListEmptyComponent={() => (
                      <YStack alignItems="center" padding="$4">
                        <SizableText color={theme ? "black" : "white"}>
                          {dataLoading ? 'Loading data...' : 'No data collections found'}
                        </SizableText>
                        {/* Debug info */}
                        {__DEV__ && (
                          <YStack alignItems="center" marginTop="$2">
                            <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$2">
                              Debug: selectedId = {selectedId || 'undefined'}
                            </SizableText>
                            <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$2">
                              dataLoading = {dataLoading.toString()}
                            </SizableText>
                            <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$2">
                              dataFolderList length = {dataFolderList?.length || 0}
                            </SizableText>
                            {dataError && (
                              <SizableText color="#ef4444" size="$2">
                                Error: {dataError.message}
                              </SizableText>
                            )}
                          </YStack>
                        )}
                      </YStack>
                    )}
                  />
                </View>
              </YStack>
            </Card>
          </View>
        )}

        {/* Device Selector Modal */}
        {showDeviceSelector && (
          <View style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <Card
              width={600}
              maxHeight="80%"
              borderRadius={20}
              padding="$6"
              borderWidth={1}
              borderColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              shadowColor={theme ? "#0f172a" : "#000000"}
              shadowOffset={{ width: 0, height: 12 }}
              shadowOpacity={theme ? 0.08 : 0.25}
              shadowRadius={24}
              elevation={12}
            >
              <YStack gap="$4">
                <XStack alignItems="center" justifyContent="space-between">
                  <YStack>
                    <H3 color={theme ? "black" : "white"}>Select Media Devices</H3>
                    {conferenceState.isInConference && (
                      <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$3" marginTop="$1">
                        Changes will apply immediately to your active stream
                      </SizableText>
                    )}
                  </YStack>
                  <Button
                    backgroundColor="transparent"
                    onPress={() => setShowDeviceSelector(false)}
                  >
                    <MaterialIcons name="close" size={24} color={theme ? "black" : "white"} />
                  </Button>
                </XStack>

                {/* Video Device Selection */}
                <YStack gap="$2">
                  <SizableText color={theme ? "black" : "white"} size="$4" fontWeight="600">
                    Camera
                  </SizableText>
                  <View style={{
                    borderWidth: 1,
                    borderColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
                    borderRadius: 8,
                    padding: 8
                  }}>
                    <select
                      value={selectedVideoDevice}
                      onChange={(e) => {
                        // Only apply device selection if in conference, otherwise just update preference
                        if (conferenceState.isInConference) {
                          applyDeviceSelection('video', e.target.value);
                        } else {
                          // Just update the selection without starting stream
                          setSelectedVideoDevice(e.target.value);
                          devicePreferencesUtils.saveDevicePreferences({
                            videoDeviceId: e.target.value,
                            audioInputDeviceId: selectedAudioInput,
                            audioOutputDeviceId: selectedAudioOutput
                          });
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: 8,
                        borderRadius: 8,
                        backgroundColor: theme ? "#ffffff" : "#334155",
                        color: theme ? "black" : "white",
                        border: 'none',
                        fontSize: 14
                      }}
                    >
                      {videoDevices.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>
                  </View>
                </YStack>

                {/* Audio Input Device Selection */}
                <YStack gap="$2">
                  <SizableText color={theme ? "black" : "white"} size="$4" fontWeight="600">
                    Microphone
                  </SizableText>
                  <View style={{
                    borderWidth: 1,
                    borderColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
                    borderRadius: 8,
                    padding: 8
                  }}>
                    <select
                      value={selectedAudioInput}
                      onChange={(e) => applyDeviceSelection('audioInput', e.target.value)}
                      style={{
                        width: '100%',
                        padding: 8,
                        borderRadius: 8,
                        backgroundColor: theme ? "#ffffff" : "#334155",
                        color: theme ? "black" : "white",
                        border: 'none',
                        fontSize: 14
                      }}
                    >
                      {audioInputDevices.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>
                  </View>
                </YStack>

                {/* Audio Output Device Selection */}
                <YStack gap="$2">
                  <SizableText color={theme ? "black" : "white"} size="$4" fontWeight="600">
                    Speaker
                  </SizableText>
                  <View style={{
                    borderWidth: 1,
                    borderColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
                    borderRadius: 8,
                    padding: 8
                  }}>
                    <select
                      value={selectedAudioOutput}
                      onChange={(e) => applyDeviceSelection('audioOutput', e.target.value)}
                      style={{
                        width: '100%',
                        padding: 8,
                        borderRadius: 8,
                        backgroundColor: theme ? "#ffffff" : "#334155",
                        color: theme ? "black" : "white",
                        border: 'none',
                        fontSize: 14
                      }}
                    >
                      {audioOutputDevices.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>
                  </View>
                </YStack>

                <XStack gap="$3" justifyContent="flex-end" marginTop="$4">
                  <Button
                    backgroundColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
                    color={theme ? "black" : "white"}
                    onPress={() => {
                      enumerateDevices();
                    }}
                    borderRadius={12}
                    padding="$3"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="refresh" size={16} color={theme ? "black" : "white"} />
                      <SizableText color={theme ? "black" : "white"} size="$3">
                        Refresh Devices
                      </SizableText>
                    </XStack>
                  </Button>
                  <Button
                    backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
                    color="white"
                    onPress={() => setShowDeviceSelector(false)}
                    borderRadius={12}
                    padding="$3"
                  >
                    <SizableText color="white" size="$3">Done</SizableText>
                  </Button>
                </XStack>
              </YStack>
            </Card>
          </View>
        )}

        {/* Shared Data Link Overlay */}
        {showSharedDataLink && sharedDataLink && (
          <View style={{
            position: 'absolute',
            top: 20,
            left: 20,
            right: 20,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            borderRadius: 12,
            padding: 16,
            zIndex: 1002,
            borderWidth: 2,
            borderColor: '#8b5cf6'
          }}>
            <YStack gap="$3">
              <XStack alignItems="center" justifyContent="space-between">
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="share" size={24} color="#8b5cf6" />
                  <SizableText color="white" size="$5" fontWeight="600">
                    Data Collection Shared
                  </SizableText>
                </XStack>
                <Button
                  backgroundColor="transparent"
                  onPress={() => setShowSharedDataLink(false)}
                >
                  <MaterialIcons name="close" size={24} color="white" />
                </Button>
              </XStack>

              <YStack gap="$2">
                <SizableText color="white" size="$4" fontWeight="600">
                  {sharedDataLink.dataInfo?.title || 'Data Collection'}
                </SizableText>
                <SizableText color="#94a3b8" size="$3">
                  {sharedDataLink.dataInfo?.type || 'General'} • {sharedDataLink.dataInfo?.count || 0} items
                </SizableText>
              </YStack>

              <TouchableOpacity
                style={{
                  backgroundColor: '#8b5cf6',
                  padding: 12,
                  borderRadius: 8,
                  alignItems: 'center'
                }}
                onPress={() => {
                  window.open(sharedDataLink.dataLink, '_blank');
                  setShowSharedDataLink(false);
                }}
              >
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="open-in-new" size={20} color="white" />
                  <SizableText color="white" fontWeight="600">
                    Open Data Collection
                  </SizableText>
                </XStack>
              </TouchableOpacity>

              <TouchableOpacity
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: 12,
                  borderRadius: 8,
                  alignItems: 'center'
                }}
                onPress={() => {
                  navigator.clipboard.writeText(sharedDataLink.dataLink);
                  Alert.alert('Copied!', 'Data link copied to clipboard');
                }}
              >
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="content-copy" size={20} color="white" />
                  <SizableText color="white">
                    Copy Link
                  </SizableText>
                </XStack>
              </TouchableOpacity>
            </YStack>
          </View>
        )}


        {/* Meeting Share Modal - Available in conference view */}
        <MeetingShareModal ref={meetingShareModalRef} />
      </DefaultScreen>
    );
  }

  // Lobby/Setup UI for desktop
  return (
    <DefaultScreen>
      {/* Meeting Share Modal - Available in lobby view */}
      <MeetingShareModal ref={meetingShareModalRef} />
      <YStack
        flex={1}
        paddingHorizontal="$6"
        paddingVertical="$8"
        width={"100%"}
      >
        {/* Header */}
        <Animatable.View
          animation="fadeInDown"
          duration={800}
        >
        <XStack alignItems="center" marginBottom="$8">
          <View style={{
            borderRadius: 24,
            padding: 24,
            marginRight: 20,
            borderWidth: 1,
            borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
            backgroundColor: theme ? "#f0f9ff" : "#0f172a",
            shadowColor: theme ? "#0ea5e9" : "#38bdf8",
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.15,
            shadowRadius: 16,
            elevation: 8
          }}>
            <MaterialIcons
              name="video-call"
              size={40}
              color={theme ? "#0ea5e9" : "#38bdf8"}
            />
          </View>
          <YStack flex={1}>
            <XStack alignItems="center" gap="$3">
              <H2 color={theme ? "black" : "white"}>
                SmartSaaS Meet
              </H2>
              <View style={{
                borderRadius: 8,
                paddingHorizontal: 12,
                paddingVertical: 4,
                borderWidth: 1,
                borderColor: connectionStatus === 'CONNECTED' ? "#10b981" :
                  connectionStatus === 'CONNECTING' ? "#f59e0b" : "#ef4444"
              }}>
                <SizableText size="$3" color={theme ? "black" : "white"} fontWeight="600">
                  {connectionStatus}
                </SizableText>
              </View>
            </XStack>
            <SizableText
              size="$5"
              color={theme ? "black" : "white"}
              marginTop="$2"
            >
              High-quality video conferencing for your team
            </SizableText>
          </YStack>
        </XStack>
        </Animatable.View>

        {/* Main Content Grid */}
        <Animatable.View
          animation="fadeInUp"
          duration={800}
          delay={200}
        >
        <YStack gap="$6" flex={1}>
          {/* Left Column - Quick Actions */}
          <YStack flex={1} gap="$4">
            <H3 color={theme ? "black" : "white"}>Quick Actions</H3>

            <XStack gap="$4">
              <CustomDialog
                title="Create New Meeting"
                confirmText="Create Meeting"
                cancelText="Cancel"
                schema={[{
                  type: 'text',
                  title: 'Meeting Name',
                  ref: 'meetingName',
                  content: {
                    placeholder: 'Enter meeting name...',
                    defaultValue: 'Quick Meeting'
                  }
                }]}
                onPress={(values) => {
                  const meetingName = values?.meetingName || 'Quick Meeting';
                  createConference(meetingName);
                }}
                trigger={
                  <Card
                    flex={1}
                    borderRadius={20}
                    padding="$6"
                    pressStyle={{ scale: 0.98 }}
                    disabled={!hasMediaPermission}
                    opacity={hasMediaPermission ? 1 : 0.6}
                    borderWidth={1}
                    borderColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
                  >
                    <YStack alignItems="center" gap="$3">
                      <MaterialIcons
                        name="add"
                        size={32}
                        color={theme ? "#0ea5e9" : "#38bdf8"}
                      />
                      <SizableText
                        size="$4"
                        color={theme ? "#0ea5e9" : "#38bdf8"}
                        fontWeight="600"
                        textAlign="center"
                      >
                        New Meeting
                      </SizableText>
                    </YStack>
                  </Card>
                }
              />

              <CustomDialog
                title="Join Meeting"
                confirmText="Join Meeting"
                cancelText="Cancel"
                schema={[{
                  type: 'text',
                  title: 'Meeting ID',
                  ref: 'meetingId',
                  content: {
                    placeholder: 'Enter meeting ID...',
                    helpText: 'Enter the meeting ID you received from the host'
                  }
                }]}
                onPress={async (values) => {
                  const roomId = values?.meetingId;
                  if (roomId && roomId.trim()) {
                    try {
                      // Use shared hook function

                      await joinConference(roomId.trim());
                    } catch (error) {
                      console.error('Error joining conference:', error);
                      Alert.alert('Error', 'Failed to join meeting');
                    }
                  } else {
                    Alert.alert('Error', 'Please enter a valid meeting ID');
                  }
                }}
                trigger={
                  <Card
                    flex={1}
                    borderRadius={20}
                    padding="$6"
                    pressStyle={{ scale: 0.98 }}
                    disabled={!hasMediaPermission}
                    opacity={hasMediaPermission ? 1 : 0.6}
                    borderWidth={1}
                    borderColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
                  >
                    <YStack alignItems="center" gap="$3">
                      <MaterialIcons
                        name="meeting-room"
                        size={32}
                        color={theme ? "#059669" : "#10b981"}
                      />
                      <SizableText
                        size="$4"
                        color={theme ? "#059669" : "#10b981"}
                        fontWeight="600"
                        textAlign="center"
                      >
                        Join Meeting
                      </SizableText>
                    </YStack>
                  </Card>
                }
              />
            </XStack>

              {/* Device Settings Button */}
              {hasMediaPermission && (
                <Animatable.View
                  animation="fadeInUp"
                  duration={600}
                  delay={300}
                >
                  <Card
                    borderRadius={20}
                    padding="$4"
                    borderWidth={1}
                    borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
                    backgroundColor={theme ? "#ffffff" : "#1e293b"}
                    shadowColor={theme ? "#0f172a" : "#000000"}
                    shadowOffset={{ width: 0, height: 8 }}
                    shadowOpacity={theme ? 0.05 : 0.15}
                    shadowRadius={16}
                    elevation={8}
                  >
                    <Button
                      backgroundColor="transparent"
                      borderWidth={1}
                      borderColor={theme ? "#8b5cf6" : "#7c3aed"}
                      color={theme ? "#8b5cf6" : "#7c3aed"}
                      onPress={() => {
                        enumerateDevices();
                        setShowDeviceSelector(true);
                      }}
                      borderRadius={12}
                      padding="$3"
                      pressStyle={{ scale: 0.98 }}
                    >
                      <XStack alignItems="center" gap="$2">
                        <MaterialIcons name="settings" size={20} color={theme ? "#8b5cf6" : "#7c3aed"} />
                        <SizableText color={theme ? "#8b5cf6" : "#7c3aed"} fontWeight="600">
                          Device Settings
                        </SizableText>
                      </XStack>
                    </Button>
                  </Card>
                </Animatable.View>
              )}

            {/* Permissions Status */}
            {!hasMediaPermission && (
              <Animatable.View
                animation="fadeInUp"
                duration={600}
                delay={400}
              >
              <Card
                borderRadius={20}
                padding="$6"
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
                backgroundColor={theme ? "#fef3c7" : "#451a03"}
                shadowColor={theme ? "#0f172a" : "#000000"}
                shadowOffset={{ width: 0, height: 12 }}
                shadowOpacity={theme ? 0.08 : 0.25}
                shadowRadius={24}
                elevation={12}
              >
                <XStack alignItems="center" gap="$3" marginBottom="$3">
                  <MaterialIcons
                    name="warning"
                    size={24}
                    color={theme ? "#d97706" : "#f59e0b"}
                  />
                  <H3
                    color={theme ? "black" : "white"}
                    fontWeight="600"
                    flex={1}
                  >
                    Permissions Required
                  </H3>
                </XStack>

                <SizableText
                  size="$4"
                  color={theme ? "black" : "white"}
                  marginBottom="$4"
                >
                  Camera and microphone access are required for video calling features.
                </SizableText>

                <XStack gap="$3">
                <Button
                  backgroundColor={theme ? "#d97706" : "#f59e0b"}
                  color="white"
                  onPress={requestMediaPermissions}
                  disabled={isLoading}
                  flex={1}
                  borderRadius={12}
                  padding="$4"
                  shadowColor="#d97706"
                  shadowOffset={{ width: 0, height: 6 }}
                  shadowOpacity={0.4}
                  shadowRadius={12}
                  elevation={8}
                  pressStyle={{ scale: 0.96 }}
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="security" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Request Permissions
                    </SizableText>
                  </XStack>
                </Button>

                  <Button
                    backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                    color="white"
                    onPress={() => {
                      console.log('Manual permission check triggered');
                      checkMediaPermissions();
                    }}
                    disabled={isLoading}
                    borderRadius={12}
                    padding="$4"
                    shadowColor="#0ea5e9"
                    shadowOffset={{ width: 0, height: 6 }}
                    shadowOpacity={0.4}
                    shadowRadius={12}
                    elevation={8}
                    pressStyle={{ scale: 0.96 }}
                  >
                    <MaterialIcons name="refresh" size={20} color="white" />
                  </Button>

                  <Button
                    backgroundColor={theme ? "#10b981" : "#059669"}
                    color="white"
                    onPress={async () => {
                      console.log('🔧 Manual camera test (Direct MeetingClient style)');
                      console.log('🔧 This is a user-initiated action');

                      try {
                        // Direct getUserMedia call with user interaction
                        console.log('🔧 Calling getUserMedia directly...');
                        const stream = await navigator.mediaDevices.getUserMedia({
                          video: selectedVideoDevice ? {
                            deviceId: { exact: selectedVideoDevice }
                          } : true,
                          audio: selectedAudioInput ? {
                            deviceId: { exact: selectedAudioInput }
                          } : true
                        });

                        console.log('🔧 Direct getUserMedia succeeded:', stream);

                        if (localVideoRef.current) {
                          localVideoRef.current.srcObject = stream;
                          await localVideoRef.current.play();
                        }

                        localStreamRef.current = stream;
                        setLocalStream(stream);
                        setIsCameraActive(true);
                        setMediaState(prev => ({ ...prev, video: true }));
                        console.log('🔧 Manual test successful');

                      } catch (error) {
                        console.error('🔧 Manual test failed:', error);
                        console.error('🔧 Error name:', error.name);
                        console.error('🔧 Error message:', error.message);
                      }
                    }}
                    disabled={isLoading}
                    borderRadius={12}
                    padding="$4"
                    shadowColor="#10b981"
                    shadowOffset={{ width: 0, height: 6 }}
                    shadowOpacity={0.4}
                    shadowRadius={12}
                    elevation={8}
                    pressStyle={{ scale: 0.96 }}
                  >
                    <MaterialIcons name="videocam" size={20} color="white" />
                  </Button>

                  <Button
                    backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
                    color="white"
                    onPress={() => {
                      console.log('🔍 Video Element Debug Info:');
                      if (localVideoRef.current) {
                        console.log('🔍 Element exists:', true);
                        console.log('🔍 Dimensions:', {
                          clientWidth: localVideoRef.current.clientWidth,
                          clientHeight: localVideoRef.current.clientHeight,
                          videoWidth: localVideoRef.current.videoWidth,
                          videoHeight: localVideoRef.current.videoHeight
                        });
                        console.log('🔍 Stream info:', {
                          srcObject: !!localVideoRef.current.srcObject,
                          paused: localVideoRef.current.paused,
                          ended: localVideoRef.current.ended,
                          readyState: localVideoRef.current.readyState,
                          networkState: localVideoRef.current.networkState
                        });
                        console.log('🔍 Style:', localVideoRef.current.style.cssText);
                      } else {
                        console.log('🔍 Element exists:', false);
                      }
                      console.log('🔍 State:', {
                        localStream: !!localStream,
                        localStreamRef: !!localStreamRef.current,
                        isCameraActive,
                        'mediaState.video': mediaState.video,
                        hasMediaPermission,
                        'conferenceState.isHost': conferenceState.isHost
                      });
                    }}
                    disabled={isLoading}
                    borderRadius={12}
                    padding="$4"
                    shadowColor="#8b5cf6"
                    shadowOffset={{ width: 0, height: 6 }}
                    shadowOpacity={0.4}
                    shadowRadius={12}
                    elevation={8}
                    pressStyle={{ scale: 0.96 }}
                  >
                    <MaterialIcons name="bug-report" size={20} color="white" />
                  </Button>

                  <Button
                    backgroundColor={theme ? "#f59e0b" : "#d97706"}
                    color="white"
                    onPress={async () => {
                      console.log('🧪 Ultra-simple camera test...');
                      try {
                        console.log('🧪 Direct getUserMedia call with minimal constraints...');

                        // Most basic possible call
                        const stream = await navigator.mediaDevices.getUserMedia({
                          video: selectedVideoDevice ? {
                            deviceId: { exact: selectedVideoDevice }
                          } : true,
                          audio: selectedAudioInput ? {
                            deviceId: { exact: selectedAudioInput }
                          } : true
                        });

                        console.log('🧪 SUCCESS! Stream obtained:', stream);
                        console.log('🧪 Stream ID:', stream.id);
                        console.log('🧪 Stream active:', stream.active);
                        console.log('🧪 Video tracks:', stream.getVideoTracks().length);

                        // Test if we can assign it to our video element
                        if (localVideoRef.current) {
                          console.log('🧪 Assigning to main video element...');
                          localVideoRef.current.srcObject = stream;
                          await localVideoRef.current.play();

                          // Update our states
                          localStreamRef.current = stream;
                          setLocalStream(stream);
                          setIsCameraActive(true);
                          setMediaState(prev => ({ ...prev, video: true }));

                          console.log('🧪 SUCCESS! Video should now be visible in main area');
                        }

                      } catch (error) {
                        console.error('🧪 Ultra-simple test failed:', error);
                        console.error('🧪 Error name:', error.name);
                        console.error('🧪 Error message:', error.message);

                        // Try even more basic
                        try {
                          console.log('🧪 Trying with empty video constraints...');
                          const stream2 = await navigator.mediaDevices.getUserMedia({ video: {} });
                          console.log('🧪 Empty constraints worked!', stream2);
                        } catch (error2) {
                          console.error('🧪 Even empty constraints failed:', error2);
                        }
                      }
                    }}
                    disabled={isLoading}
                    borderRadius={12}
                    padding="$4"
                    shadowColor="#f59e0b"
                    shadowOffset={{ width: 0, height: 6 }}
                    shadowOpacity={0.4}
                    shadowRadius={12}
                    elevation={8}
                    pressStyle={{ scale: 0.96 }}
                  >
                    <MaterialIcons name="science" size={20} color="white" />
                  </Button>
                </XStack>
              </Card>
              </Animatable.View>
            )}

            {/* Camera Test */}
            {hasMediaPermission && (
              <Animatable.View
                animation="fadeInUp"
                duration={600}
                delay={600}
              >
              <Card
                borderRadius={20}
                padding="$6"
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
                backgroundColor={theme ? "#d1fae5" : "#064e3b"}
                shadowColor={theme ? "#0f172a" : "#000000"}
                shadowOffset={{ width: 0, height: 12 }}
                shadowOpacity={theme ? 0.08 : 0.25}
                shadowRadius={24}
                elevation={12}
              >
                <XStack alignItems="center" gap="$3" marginBottom="$3">
                  <MaterialIcons
                    name="check-circle"
                    size={24}
                    color={theme ? "#059669" : "#10b981"}
                  />
                  <H3
                    color={theme ? "black" : "white"}
                    fontWeight="600"
                    flex={1}
                  >
                    Ready for Video Calls
                  </H3>
                </XStack>

                <SizableText
                  size="$4"
                  color={theme ? "black" : "white"}
                  marginBottom="$4"
                >
                  Your camera and microphone are accessible. You can now create or join meetings.
                </SizableText>

                <Button
                  backgroundColor={theme ? "#059669" : "#10b981"}
                  color="white"
                  onPress={async () => {
                    try {
                      const stream = await navigator.mediaDevices.getUserMedia({
                        video: {
                          deviceId: selectedVideoDevice ? { exact: selectedVideoDevice } : undefined,
                          width: { min: 640, ideal: 1280 },
                          height: { min: 480, ideal: 720 },
                          frameRate: { min: 15, ideal: 30 }
                        },
                        audio: selectedAudioInput ? {
                          deviceId: { exact: selectedAudioInput }
                        } : true
                      });
                      if (stream) {
                        setIsCameraActive(true);
                        setMediaState(prev => ({ ...prev, video: true }));
                        Alert.alert('Camera Test', 'Camera access successful! Video feed is now active.');
                      } else {
                        Alert.alert('Camera Test Failed', 'Failed to initialize video stream.');
                      }
                    } catch (error) {
                      console.error('Camera test error:', error);
                      Alert.alert('Camera Test Failed', 'There was an issue accessing your camera.');
                    }
                  }}
                  disabled={isLoading}
                  borderRadius={12}
                  padding="$4"
                  shadowColor="#059669"
                  shadowOffset={{ width: 0, height: 6 }}
                  shadowOpacity={0.4}
                  shadowRadius={12}
                  elevation={8}
                  pressStyle={{ scale: 0.96 }}
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="videocam" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Test Camera Access
                    </SizableText>
                  </XStack>
                </Button>
              </Card>
              </Animatable.View>
            )}
          </YStack>

          {/* Right Column - Recent Meetings & Dev Tools */}
          <YStack flex={1} gap="$4">
            <H3 color={theme ? "black" : "white"}>Recent Meetings</H3>

            <Animatable.View
              animation="fadeInUp"
              duration={600}
              delay={400}
            >
            <Card
              borderRadius={20}
              padding="$6"
              borderWidth={1}
              borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              shadowColor={theme ? "#0f172a" : "#000000"}
              shadowOffset={{ width: 0, height: 12 }}
              shadowOpacity={theme ? 0.08 : 0.25}
              shadowRadius={24}
              elevation={12}
            >
              {meetingsLoading ? (
                <YStack alignItems="center" padding="$4">
                  <SizableText
                    size="$4"
                    color={theme ? "black" : "white"}
                    textAlign="center"
                  >
                    Loading recent meetings...
                  </SizableText>
                </YStack>
              ) : meetingsError ? (
                <YStack alignItems="center" padding="$4">
                  <MaterialIcons
                    name="error-outline"
                    size={32}
                    color={theme ? "#dc2626" : "#f87171"}
                    style={{ marginBottom: 12 }}
                  />
                  <SizableText
                    size="$4"
                    color={theme ? "#dc2626" : "#f87171"}
                    textAlign="center"
                    marginBottom="$2"
                  >
                    Failed to load recent meetings
                  </SizableText>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    textAlign="center"
                  >
                    {meetingsError?.message || 'Please try again later'}
                  </SizableText>
                </YStack>
              ) : recentMeetings?.data?.meetings?.length > 0 ? (
                <YStack gap="$4">
                  <H4 color={theme ? "black" : "white"} marginBottom="$2">
                    Recent Meetings ({recentMeetings.data.meetings.length})
                  </H4>
                  {recentMeetings.data.meetings.map((meeting, index) => (
                    <Animatable.View
                      key={meeting._id}
                      animation="fadeInUp"
                      duration={400}
                      delay={index * 100}
                    >
                      <Card
                        padding="$4"
                        borderRadius={12}
                        borderWidth={1}
                        borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
                        backgroundColor={theme ? "#f8fafc" : "#334155"}
                        shadowColor={theme ? "#0f172a" : "#000000"}
                        shadowOffset={{ width: 0, height: 4 }}
                        shadowOpacity={theme ? 0.05 : 0.15}
                        shadowRadius={8}
                        elevation={4}
                        pressStyle={{ scale: 0.98 }}
                        onPress={() => {
                          console.log('Meeting clicked:', meeting);
                          // TODO: Add navigation to meeting details or rejoin functionality
                        }}
                      >
                        <YStack gap="$2">
                          <XStack alignItems="center" justifyContent="space-between">
                            <SizableText
                              color={theme ? "black" : "white"}
                              size="$4"
                              fontWeight="600"
                              flex={1}
                              numberOfLines={1}
                            >
                              {meeting.title || 'Untitled Meeting'}
                            </SizableText>
                            <View
                              style={{
                                backgroundColor: meeting.status === 'completed' 
                                  ? (theme ? "#d1fae5" : "#064e3b")
                                  : meeting.status === 'scheduled'
                                  ? (theme ? "#dbeafe" : "#0c4a6e")
                                  : (theme ? "#fef3c7" : "#451a03"),
                                paddingHorizontal: 8,
                                paddingVertical: 4,
                                borderRadius: 8
                              }}
                            >
                              <SizableText
                                size="$2"
                                fontWeight="600"
                                color={meeting.status === 'completed'
                                  ? (theme ? "#059669" : "#10b981")
                                  : meeting.status === 'scheduled'
                                  ? (theme ? "#0ea5e9" : "#38bdf8")
                                  : (theme ? "#d97706" : "#fbbf24")
                                }
                              >
                                {meeting.status || 'pending'}
                              </SizableText>
                            </View>
                          </XStack>
                          
                          <XStack alignItems="center" gap="$3">
                            <XStack alignItems="center" gap="$1">
                              <MaterialIcons
                                name="schedule"
                                size={16}
                                color={theme ? "#64748b" : "#94a3b8"}
                              />
                              <SizableText
                                color={theme ? "#64748b" : "#94a3b8"}
                                size="$3"
                              >
                                {meeting.startTime 
                                  ? new Date(meeting.startTime).toLocaleDateString()
                                  : 'No date'
                                }
                              </SizableText>
                            </XStack>
                            
                            {meeting.duration && (
                              <XStack alignItems="center" gap="$1">
                                <MaterialIcons
                                  name="timer"
                                  size={16}
                                  color={theme ? "#64748b" : "#94a3b8"}
                                />
                                <SizableText
                                  color={theme ? "#64748b" : "#94a3b8"}
                                  size="$3"
                                >
                                  {meeting.duration}m
                                </SizableText>
                              </XStack>
                            )}
                            
                            {meeting.participants?.length > 0 && (
                              <XStack alignItems="center" gap="$1">
                                <MaterialIcons
                                  name="people"
                                  size={16}
                                  color={theme ? "#64748b" : "#94a3b8"}
                                />
                                <SizableText
                                  color={theme ? "#64748b" : "#94a3b8"}
                                  size="$3"
                                >
                                  {meeting.participants.length}
                                </SizableText>
                              </XStack>
                            )}
                          </XStack>
                          
                          {meeting.location && (
                            <XStack alignItems="center" gap="$1">
                              <MaterialIcons
                                name="location-on"
                                size={16}
                                color={theme ? "#64748b" : "#94a3b8"}
                              />
                              <SizableText
                                color={theme ? "#64748b" : "#94a3b8"}
                                size="$3"
                                numberOfLines={1}
                                flex={1}
                              >
                                {meeting.location}
                              </SizableText>
                            </XStack>
                          )}
                        </YStack>
                      </Card>
                    </Animatable.View>
                  ))}
                  
                  {recentMeetings.data.pagination?.totalCount > 5 && (
                    <Button
                      backgroundColor="transparent"
                      borderWidth={1}
                      borderColor={theme ? "#0ea5e9" : "#38bdf8"}
                      color={theme ? "#0ea5e9" : "#38bdf8"}
                      borderRadius={12}
                      padding="$3"
                      onPress={() => {
                        // TODO: Navigate to full meetings list
                        navigation.navigate('MeetingList');
                      }}
                      pressStyle={{ scale: 0.98 }}
                    >
                      <SizableText color={theme ? "#0ea5e9" : "#38bdf8"} fontWeight="600">
                        View All Meetings ({recentMeetings.data.pagination.totalCount})
                      </SizableText>
                    </Button>
                  )}
                </YStack>
              ) : (
                <YStack alignItems="center" padding="$4">
                  <MaterialIcons
                    name="video-call"
                    size={48}
                    color={theme ? "#64748b" : "#94a3b8"}
                    style={{ marginBottom: 16 }}
                  />
                  <SizableText
                    size="$4"
                    color={theme ? "black" : "white"}
                    textAlign="center"
                    fontWeight="600"
                    marginBottom="$2"
                  >
                    No recent meetings found
                  </SizableText>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    textAlign="center"
                  >
                    Start a new meeting to get started!
                  </SizableText>
                </YStack>
              )}
            </Card>
            </Animatable.View>

            {/* Development Test Controls */}
            {__DEV__ && (
              <Animatable.View
                animation="fadeInUp"
                duration={600}
                delay={800}
              >
              <Card
                borderRadius={20}
                padding="$6"
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
                backgroundColor={theme ? "#ede9fe" : "#312e81"}
                shadowColor={theme ? "#0f172a" : "#000000"}
                shadowOffset={{ width: 0, height: 12 }}
                shadowOpacity={theme ? 0.08 : 0.25}
                shadowRadius={24}
                elevation={12}
              >
                <XStack alignItems="center" gap="$3" marginBottom="$3">
                  <MaterialIcons
                    name="bug-report"
                    size={24}
                    color={theme ? "#d97706" : "#f59e0b"}
                  />
                  <H3
                    color={theme ? "black" : "white"}
                    fontWeight="600"
                    flex={1}
                  >
                    Development Test Controls
                  </H3>
                </XStack>

                <SizableText
                  size="$4"
                  color={theme ? "black" : "white"}
                  marginBottom="$4"
                >
                  Test camera functionality and meeting features
                </SizableText>

                <YStack gap="$3">
                  <XStack gap="$2" flexWrap="wrap">
                    <Button
                      backgroundColor={theme ? "#059669" : "#10b981"}
                      color="white"
                      size="$3"
                      onPress={async () => {
                        if (isCameraActive) {
                          // Stop camera
                          if (localStream) {
                            localStream.getTracks().forEach(track => track.stop());
                            setLocalStream(null);
                          }
                          if (localVideoRef.current) {
                            localVideoRef.current.srcObject = null;
                          }
                          setIsCameraActive(false);
                          setMediaState(prev => ({ ...prev, video: false }));
                        } else {
                          // Start camera
                          const stream = await navigator.mediaDevices.getUserMedia({
                            video: {
                              deviceId: selectedVideoDevice ? { exact: selectedVideoDevice } : undefined,
                              width: { min: 640, ideal: 1280 },
                              height: { min: 480, ideal: 720 },
                              frameRate: { min: 15, ideal: 30 }
                            },
                            audio: selectedAudioInput ? {
                              deviceId: { exact: selectedAudioInput }
                            } : true
                          });
                          if (stream) {
                            setIsCameraActive(true);
                            setMediaState(prev => ({ ...prev, video: true }));
                          }
                        }
                      }}
                      disabled={!hasMediaPermission}
                      borderRadius={12}
                      shadowColor="#059669"
                      shadowOffset={{ width: 0, height: 4 }}
                      shadowOpacity={0.3}
                      shadowRadius={8}
                      elevation={6}
                      pressStyle={{ scale: 0.98 }}
                    >
                      <XStack alignItems="center" gap="$2">
                        <MaterialIcons name="videocam" size={16} color="white" />
                        <SizableText color="white" size="$3">
                          {isCameraActive ? 'Stop Camera' : 'Start Camera'}
                        </SizableText>
                      </XStack>
                    </Button>

                    <Button
                      backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                      color="white"
                      size="$3"
                      onPress={toggleCameraFacing}
                      disabled={!hasMediaPermission}
                      borderRadius={12}
                      shadowColor="#0ea5e9"
                      shadowOffset={{ width: 0, height: 4 }}
                      shadowOpacity={0.3}
                      shadowRadius={8}
                      elevation={6}
                      pressStyle={{ scale: 0.98 }}
                    >
                      <XStack alignItems="center" gap="$2">
                        <MaterialIcons name="flip-camera-ios" size={16} color="white" />
                        <SizableText color="white" size="$3">
                          Switch Camera
                        </SizableText>
                      </XStack>
                    </Button>
                  </XStack>

                  <SizableText
                    size="$3"
                    color={theme ? "black" : "white"}
                    textAlign="center"
                  >
                    Camera: {isCameraActive ? 'Active' : 'Inactive'} |
                    Facing: {facing} |
                    Permissions: {hasMediaPermission ? 'Granted' : 'Required'}
                  </SizableText>
                </YStack>
              </Card>
              </Animatable.View>
            )}
          </YStack>
        </YStack>
        </Animatable.View>
      </YStack>



      {/* Device Selector Modal - Lobby View */}
      {showDeviceSelector && (
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <Card
            width={600}
            maxHeight="80%"
            borderRadius={20}
            padding="$6"
            borderWidth={1}
            borderColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
            backgroundColor={theme ? "#ffffff" : "#1e293b"}
            shadowColor={theme ? "#0f172a" : "#000000"}
            shadowOffset={{ width: 0, height: 12 }}
            shadowOpacity={theme ? 0.08 : 0.25}
            shadowRadius={24}
            elevation={12}
          >
            <YStack gap="$4">
              <XStack alignItems="center" justifyContent="space-between">
                <H3 color={theme ? "black" : "white"}>Select Media Devices</H3>
                <Button
                  backgroundColor="transparent"
                  onPress={() => setShowDeviceSelector(false)}
                >
                  <MaterialIcons name="close" size={24} color={theme ? "black" : "white"} />
                </Button>
              </XStack>

              {/* Video Device Selection */}
              <YStack gap="$2">
                <SizableText color={theme ? "black" : "white"} size="$4" fontWeight="600">
                  Camera
                </SizableText>
                <View style={{
                  borderWidth: 1,
                  borderColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
                  borderRadius: 8,
                  padding: 8
                }}>
                  <select
                    value={selectedVideoDevice}
                    onChange={(e) => {
                      setSelectedVideoDevice(e.target.value);
                    }}
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 8,
                      backgroundColor: theme ? "#ffffff" : "#334155",
                      color: theme ? "black" : "white",
                      border: 'none',
                      fontSize: 14
                    }}
                  >
                    {videoDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                      </option>
                    ))}
                  </select>
                </View>
              </YStack>

              {/* Audio Input Device Selection */}
              <YStack gap="$2">
                <SizableText color={theme ? "black" : "white"} size="$4" fontWeight="600">
                  Microphone
                </SizableText>
                <View style={{
                  borderWidth: 1,
                  borderColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
                  borderRadius: 8,
                  padding: 8
                }}>
                  <select
                    value={selectedAudioInput}
                    onChange={(e) => {
                      setSelectedAudioInput(e.target.value);
                    }}
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 8,
                      backgroundColor: theme ? "#ffffff" : "#334155",
                      color: theme ? "black" : "white",
                      border: 'none',
                      fontSize: 14
                    }}
                  >
                    {audioInputDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                      </option>
                    ))}
                  </select>
                </View>
              </YStack>

              {/* Audio Output Device Selection */}
              <YStack gap="$2">
                <SizableText color={theme ? "black" : "white"} size="$4" fontWeight="600">
                  Speaker
                </SizableText>
                <View style={{
                  borderWidth: 1,
                  borderColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
                  borderRadius: 8,
                  padding: 8
                }}>
                  <select
                    value={selectedAudioOutput}
                    onChange={(e) => {
                      setSelectedAudioOutput(e.target.value);
                    }}
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 8,
                      backgroundColor: theme ? "#ffffff" : "#334155",
                      color: theme ? "black" : "white",
                      border: 'none',
                      fontSize: 14
                    }}
                  >
                    {audioOutputDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                      </option>
                    ))}
                  </select>
                </View>
              </YStack>

              <XStack gap="$3" justifyContent="flex-end" marginTop="$4">
                <Button
                  backgroundColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
                  color={theme ? "black" : "white"}
                  onPress={() => {
                    enumerateDevices();
                  }}
                  borderRadius={12}
                  padding="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="refresh" size={16} color={theme ? "black" : "white"} />
                    <SizableText color={theme ? "black" : "white"} size="$3">
                      Refresh Devices
                    </SizableText>
                  </XStack>
                </Button>
                <Button
                  backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
                  color="white"
                  onPress={() => setShowDeviceSelector(false)}
                  borderRadius={12}
                  padding="$3"
                >
                  <SizableText color="white" size="$3">Done</SizableText>
                </Button>
              </XStack>
            </YStack>
          </Card>
        </View>
      )}

      {/* Test Modal */}
      {showTestModal && (
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <Card
            width={400}
            borderRadius={20}
            padding="$6"
            borderWidth={1}
            borderColor={theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
          >
            <YStack gap="$4">
              <XStack alignItems="center" justifyContent="space-between">
                <H3 color={theme ? "black" : "white"}>Test Modal</H3>
                <Button
                  backgroundColor="transparent"
                  onPress={() => setShowTestModal(false)}
                >
                  <MaterialIcons name="close" size={24} color={theme ? "black" : "white"} />
                </Button>
              </XStack>
              <SizableText color={theme ? "black" : "white"}>
                This is a test modal to verify modal functionality works.
              </SizableText>
              <Button
                backgroundColor="#059669"
                color="white"
                onPress={() => setShowTestModal(false)}
                borderRadius={8}
              >
                Close
              </Button>
            </YStack>
          </Card>
        </View>
      )}
    </DefaultScreen>
  );
};

export default Meet;
